import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import Pagination, { getPageSize } from '../../Pagination';
import { fmtDateTime, money } from '../../../utils/region';
import OverflowChips, { OChip } from '../../OverflowChips';
import type {
  AwBankLine, AwExpense, AwPayable, AwPayableBody, AwPayableDetail,  AwPayableFromUpload, AwPayablePayment,
  AwPurchaseNoteKind, AwPurchaseNotePage, AwPurchaseNoteRow, AwPurchaseOrder, AwPurchaseOrderBody, AwPurchaseOrderDetail, AwPurchaseOrderFilter,
  AwPurchaseNote, AwNoteBookable, AwNoteDetail, AwNoteDoors, AwPoDoors, AwPurchaseNoteEditBody, AwRecordDoors,
  AwPurchaseOrderLineBody, AwPurchaseOrderPage,
} from '../../../types';
import type { AwCtx } from '../ctx';
import { AttachmentsBtn, RecordEvidence, useAttachmentCounts } from '../attachments';   // owner 2026-11-02: a record's supporting files and linked records; the same deliberate cycle as the rest
import { amt, baht, btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pill, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, actBtn, cancelBtn, chip, chipOn, emptyLine, field, isoDate, lbl, r2, todayIso, useDialogClose } from '../dialog';
import { DocEntriesBtn, DocEntriesDialog } from '../docEntries';
import NewDocBtn from '../NewDocBtn';
import RowMenu, { type RowMenuEntry } from '../RowMenu';
import { NoCell, NoNumber, SmallLine, StatusCell } from '../listCells';   // the same deliberate cycle as sourceDoc.tsx's: every cross-reference is inside a body
import { isReadOnlySeat } from './fixedAssets/shared';
import { SortTh, sortArgs, useListSort } from '../listSort';   // batch 12 tail T3 (FS-11): the three lists' headers sort, and stay
import { epayApi, EPAY_ERRORS, EPAY_METHODS, EPAY_FILE_MAX, EPAY_FILE_TYPES, EPAY_FILES_PER_PAYMENT } from '../../../utils/api/expensePayments';
import type { EpayMethod, EpayOptionBill, EpayOptions, EpayRecorded, EpayReversibility, EpayWhtCandidates } from '../../../utils/api/expensePayments';
// ⛔ A deliberate import cycle, the house's (sourceDoc.tsx ↔ docEntries.tsx): the record window draws a bill
// with this file's sections and dialogs, and Supplier bills opens that window. Every cross-reference is
// inside a component body, so nothing is read while either module is still evaluating.
import { ExpenseRecordWindow, useRecordDoors, useRecordParam } from './ExpenseRecords';
// Batch 11 W3 R6 (P6c): the settlement's way on, on a payment's row and on the bill — Generate for one line (settleApi), the
// month's hand vouchers (Banking's SettleAckDialog, its words settleSkipText, the other bill's window OpenBillBtn) and
// "Reverse JV-…" (R3's ReverseEntryBtn). The same deliberate cycle (Banking.tsx imports this file's PaymentDialog):
// every cross-reference is inside a component body.
import { settleApi, closedOf, BOOK_BLOCK_CODES } from '../../../utils/api/settlements';
import { OpenBillBtn, SettleAckDialog, settleSkipText } from './Banking';
import { ReverseEntryBtn } from '../reverseEntryBtn';
// Batch 12 tail T1 (FS-6): the note window's "Open the bill in Expenses" — the voucher windows' own way there (sourceDoc.tsx), through
// the unsaved guard. The same deliberate cycle as above (sourceDoc.tsx draws a note with this file's NoteFacts): inside component bodies.
import { stillStands, useGoToExpenseRecord } from '../sourceDoc';

// ── Purchases (owner 2026-10-13) ────────────────────────────────────────────────────────────────
// Three REGISTERS of the client company, read and written through /api/acct-workspace/payables and
// /purchase-orders: supplier bills owed (payables) with the payments made on them, the purchase
// debit/credit notes that adjust a payable, and purchase orders that may be converted into a bill.
// Nothing here is the firm's: not its bank tables, not the statutory ledger (manual entries stay its
// only feed), not the company's invoices or receipts. VAT and WHT are typed from the bill — the UI
// only checks they are non-negative and that WHT does not exceed the total. The ONLY arithmetic on
// this side is the register's own rule, shown live so the operator sees what the server will store:
// to pay = total − WHT; a PO line = qty × unit price; a PO total = Σ lines; all at 2 dp. Outstanding
// and status come from the server, never recomputed here.
//
// Supplier bills (merchant_expense_uploads) stay what they were: the bills the company uploaded,
// approved in its portal. An upload becomes a payable only when the operator says so ("Create bill"),
// which pre-fills the form with the upload's own OCR fields and links the two (one payable per upload).

type TF = (key: string, opts?: Record<string, unknown>) => string;
/** A bill's settlement lock (not in a closed period), in words: "reverse it first" — or, where its reversal is raised
 *  already (W3.1, the state lens' low), "post R… first" (some raised: post those, reverse the rest). `fields`: the VAT
 *  fields' form (aw_bill_settle_lock_fields*). The server's own why says the same (expenseRecords.stillPostedWhy). */
export function billLockText(t: TF, entries: { entryId: string; docNo: string; reversalDraft?: string }[], fields: boolean): string {
  const no = (e: { entryId: string; docNo: string }) => e.docNo || e.entryId.slice(0, 8);
  const docNo = entries.map(no).join(', ');
  const rv = entries.filter(e => !!e.reversalDraft), rest = entries.filter(e => !e.reversalDraft);
  if (!rv.length) return fields ? t('aw_bill_settle_lock_fields', { docNo }) : t('aw_bill_settle_lock', { docNo });
  const rvs = rv.map(e => e.reversalDraft).join(', ');
  if (rest.length) {
    const others = rest.map(no).join(', ');
    return fields ? t('aw_bill_settle_lock_fields_some_rev', { docNo, rv: rvs, others }) : t('aw_bill_settle_lock_some_rev', { docNo, rv: rvs, others });
  }
  return fields ? t('aw_bill_settle_lock_fields_rev', { docNo, rv: rvs }) : t('aw_bill_settle_lock_rev', { docNo, rv: rvs });
}
const PO_FILTERS: AwPurchaseOrderFilter[] = ['all', 'draft', 'approved', 'received', 'billed', 'cancelled'];
const MAX_STR = 200;
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
const poPill = (s: string): React.CSSProperties => s === 'billed' ? pillGreen : s === 'cancelled' ? pillRed : s === 'approved' ? pillBlue : s === 'received' ? pillAmber : pillGrey;
// Days past due: amber up to 60, red beyond — the aging pane's own reading of the same buckets.
const overduePill = (days: number): React.CSSProperties => days > 60 ? pillRed : pillAmber;
const when = (v: number) => v ? fmtDateTime(v) : '';
const isLive = (p: AwPayable) => p.status === 'open' || p.status === 'partial';
const fin = (s: string): number | null => { if (String(s).trim() === '') return null; const n = Number(s); return Number.isFinite(n) ? n : NaN; };
const okStr = (s: string) => s.length <= MAX_STR;
/** The route's error turned into a sentence; a 409 names the state the register is in. */
const perr = (e: any, t: TF): string => {
  const msg = String(e?.message || '');
  if (e?.status === 409) return `${t('aw_pay_err_conflict')}${msg ? ` — ${msg}` : ''}`;
  if (e?.status === 404) return t('aw_pay_err_not_found');
  return msg || t('aw_bank_err_generic');
};
/** A note's number as the accent link that opens its window (the PO list's number, the same face). */
const noteLink: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 'inherit', fontVariantNumeric: 'tabular-nums' };
const seg = (on: boolean): React.CSSProperties => ({ ...btn, flex: 1, justifyContent: 'center', ...(on ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) });
/** Cancel / Close inside a Dialog, routed through the SHELL's close gate (dialog.tsx useCloseGate),
 *  so this exit asks the same question the X, Esc and the backdrop ask — once, and in one place.
 *  ⛔ A COMPONENT, not a `useDialogClose()` in the dialog's own body: the gate arrives by context
 *  from INSIDE the Dialog's tree, which a hook in the component that RENDERS the Dialog cannot see
 *  (dialog.tsx's own Foot is a component for exactly that reason).
 *  ⛔ It never saves and it never discards by itself: it only calls the gate. */
const GateCancel = ({ onClose, busy, label }: { onClose: () => void; busy?: boolean; label: string }) => {
  const close = useDialogClose(onClose);
  return <button onClick={close} disabled={busy} style={cancelBtn}>{label}</button>;
};
const Field = ({ label, children, span }: { label: string; children: React.ReactNode; span?: number }) => (
  <div style={span ? { gridColumn: `span ${span}` } : undefined}><label style={lbl}>{label}</label>{children}</div>
);

/** Every page of a list the server pages (limit 200 = its largest page), so a picker never stops
 *  silently at the newest 200 rows (client review 2026-10-13). The page cap is a safety stop only. */
async function loadAllPages<T>(fetchPage: (page: number) => Promise<{ rows: T[]; total: number }>): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 50; page++) {
    const d = await fetchPage(page);
    const rows = d.rows || [];
    out.push(...rows);
    if (!rows.length || out.length >= (Number(d.total) || 0)) break;
  }
  return out;
}
/** Every payable of the register, all statuses: the contract has no "payable for upload X" lookup and
 *  no notes list, so the upload → bill link and the notes register are read off the payables themselves.
 *  A bill is linked to its upload whatever its status — the server refuses a second payable on the same
 *  upload even when the first was voided. */
const loadAllPayables = (clientId: string): Promise<AwPayable[]> =>
  loadAllPages<AwPayable>(page => api.awPayables(clientId, { status: 'all', page, limit: 200 }));
const uploadedAt = (v: string | number): number => typeof v === 'number' ? v : (/^\d+$/.test(String(v || '')) ? Number(v) : (Date.parse(String(v || '')) / 1000 || 0));
/** The uploads a bill may be created from: this company's pending and approved uploads (the server
 *  filters one status per call, so two calls, each paged to the end) that no payable links yet, newest first. */
async function loadBillableUploads(clientId: string): Promise<AwExpense[]> {
  const [pending, approved, pays] = await Promise.all([
    loadAllPages<AwExpense>(page => api.awExpenses(clientId, 'pending', { page, limit: 200 })),
    loadAllPages<AwExpense>(page => api.awExpenses(clientId, 'approved', { page, limit: 200 })),
    loadAllPayables(clientId),
  ]);
  const linked = byUpload(pays);
  // A scan the firm cancelled (batch 7 R3b) takes no bill — the server refuses one.
  return [...pending, ...approved].filter(u => !linked.has(u.id) && !u.cancelled).sort((a, b) => uploadedAt(b.uploaded_at) - uploadedAt(a.uploaded_at));
}
/** The unmatched money-out lines of the company's statements, every page (the API pages by state only).
 *  Exported for the expense record window's Pay (batch 7 R3b), which pays from a line the same way. */
export async function loadUnmatchedDebitLines(clientId: string): Promise<AwBankLine[]> {
  const rows = await loadAllPages<AwBankLine>(page => api.awBankLines(clientId, { state: 'unmatched', page, limit: 200 }));
  return rows.filter(l => Number(l.debit) > 0);
}
/** The server's currency rule (readCurrency): exactly three letters, or blank for the company's default —
 *  checked here so the refusal is read before the request, not after it. */
const curOk = (c: string) => !c.trim() || /^[A-Za-z]{3}$/.test(c.trim());
/** A unit price as typed: 2 dp like money, more when the operator typed more — the server keeps a
 *  per-unit price as typed and rounds only the line amount. */
const unitPrice = (v: any) => { const dp = (String(v).split('.')[1] || '').length; return money(Number(v) || 0, Math.min(6, Math.max(2, dp))); };
const byUpload = (rows: AwPayable[]): Map<string, AwPayable> => {
  const m = new Map<string, AwPayable>();
  for (const p of rows) if (p.expense_upload_id) m.set(p.expense_upload_id, p);
  return m;
};
const uploadLabel = (u: AwExpense) => [u.doc_date ? dmy(u.doc_date) : '', u.vendor, u.amount === null || u.amount === undefined ? '' : amt(u.amount), u.filename].filter(Boolean).join(' · ');
const payableLabel = (p: AwPayable) => `${p.bill_no} · ${p.vendor_name} · ${amt(p.outstanding)} ${p.currency}`;

// ── New / edit bill dialog ──────────────────────────────────────────────────────────────────────
export function BillDialog({ ctx, payable, upload, onClose, onSaved }: {
  ctx: AwCtx; payable?: AwPayable; upload?: AwExpense; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const edit = !!payable;
  // A paid or void bill is finished: the server takes its note alone (409 on anything else), so the
  // form opens with every other field read-only and sends { note } by itself (client review 2026-10-13).
  const noteOnly = edit && !isLive(payable!);
  const [vendor, setVendor] = useState(payable?.vendor_name || '');
  const [taxId, setTaxId] = useState(payable?.vendor_tax_id || '');
  const [billNo, setBillNo] = useState(payable?.bill_no || '');
  const [billDate, setBillDate] = useState(payable?.bill_date || todayIso());
  const [dueDate, setDueDate] = useState(payable?.due_date || '');
  // Blank = the company's own default currency, applied by the server (readCurrency → currencyCode);
  // nothing is guessed from the bills already in the register.
  const [currency, setCurrency] = useState(payable?.currency || '');
  const [total, setTotal] = useState(payable ? String(payable.total_amount) : '');
  const [vat, setVat] = useState(payable ? String(payable.vat_amount) : '');
  const [wht, setWht] = useState(payable ? String(payable.wht_amount) : '');
  const [noteTxt, setNoteTxt] = useState(payable?.note || '');
  const [uploadId, setUploadId] = useState(upload?.id || '');
  // ── What the bill is FOR, line by line (phase 4b, owner 2026-10-15) ──────────────────────────
  // Each line may name the charge we call it (the identity the books key on) and/or the account it
  // books to, chosen from THIS client's own chart. Nothing is required: a bill with no lines is
  // recorded exactly as bills were before, and books on the supplier's own account.
  type BillLine = { description: string; qty: string; unit_price: string; doc_item_code: string; charge_code: string; account_code: string };
  const blank = (): BillLine => ({ description: '', qty: '1', unit_price: '', doc_item_code: '', charge_code: '', account_code: '' });
  const [lines, setLines] = useState<BillLine[]>([]);
  const [accounts, setAccounts] = useState<{ code: string; name_th: string; name_en: string }[]>([]);
  // What the lines add up to, against the bill without its VAT — the figure the posting engine
  // ties against (services/draftEntries.ts splits by item only on an exact tie).
  const lineSum = r2(lines.reduce((s2, l) => s2 + r2((Number(l.qty) || 0) * (Number(l.unit_price) || 0)), 0));
  const netBase = r2((Number(total) || 0) - (Number(vat) || 0));
  const linesTie = Math.abs(lineSum - netBase) < 0.005;
  const [options, setOptions] = useState<AwExpense[] | null>(edit ? [] : null);
  const [filling, setFilling] = useState(false);
  // ⛔ (W2 ship, the money review's high) The WHT the bill's payments already withheld (GET /payables/:id whtWithheld) —
  // the floor of its printed WHT. PATCH /payables/:id refuses a WHT lowered below it (409 wht_below_withheld: to_pay
  // would rise by what was withheld and the supplier be paid it twice), so the form shows it and offers no Save below it.
  const [whtFloor, setWhtFloor] = useState(0);
  // ⛔ (Batch 11 W3 R6, W3-15) A BOOKED bill whose payment is settled in the books (GET /payables/:id settleLock): an edit
  // that moves its VAT line (number, date, supplier, tax id, total, VAT, currency, lines) is refused settlement_posted
  // until that settlement is reversed — so those fields stay as they are here and the form says why, and a Save sends
  // only what the server takes (due date, WHT, note).
  const [settleLock, setSettleLock] = useState<AwPayableDetail['settleLock']>(null);
  // (Batch 11 W4 P9, LB-49) …and every other refusal Unbook would give (its VAT line posted, filed, an asset, a hand draft):
  // a VAT-moving edit goes through the same withdrawal, so those fields stay as they are too.
  const [unbookRef, setUnbookRef] = useState<AwPayableDetail['unbookRefusal']>(null);
  useEffect(() => {
    let alive = true;
    // The expense accounts this client may name. Read from the workspace's own endpoint, which
    // answers with the client's chart — never the firm's.
    api.awExpenseAccountOptions(ctx.clientId).then((d: any) => { if (alive) setAccounts(d?.accounts || []); }).catch(() => { /* no picker, no harm */ });
    if (edit && payable) {
      api.awPayable(ctx.clientId, payable.id).then((d: any) => {
        if (!alive) return;
        setWhtFloor(Number(d?.whtWithheld) || 0);
        setSettleLock(d?.settleLock || null);
        setUnbookRef(d?.unbookRefusal || null);
        setLines((d?.lines || []).map((l: any) => ({
          description: String(l.description || ''), qty: String(l.qty ?? ''), unit_price: String(l.unit_price ?? ''),
          doc_item_code: String(l.doc_item_code || ''), charge_code: String(l.charge_code || ''), account_code: String(l.account_code || ''),
        })));
      }).catch(() => { /* the lines simply do not show */ });
    }
    return () => { alive = false; };
    // ⛔ The bill's ID, never the object: a prop object is a new reference on every render, so
    // depending on it re-runs this effect for ever — each run discarding the answer the one before
    // it was waiting for, which is why the editor came up empty while the network filled with
    // thousands of identical requests.
  }, [ctx.clientId, edit, payable?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12. The flag the shell's close gate reads (dialog.tsx), in the shape of the house precedent
  // (Admin/JobRequiredDocs.tsx): the EDIT PATH sets it and nothing else does.
  // ⛔ NOT a diff against the values the form opened with, and this dialog is why: it fills ITSELF
  // from the upload's stored OCR before the operator has touched a key (the mount effect below
  // calls fill()), and an edit opens pre-filled with the bill and its lines. A diff would report
  // every one of those as unsaved work, and an operator who is warned when they typed nothing
  // learns to click the warning away — which is worse than no warning at all.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  // The picker lists this company's pending/approved uploads that have no payable yet (every page, not
  // the newest 200); the one handed in from Supplier bills is kept even when a reload would hide it (it
  // has no bill — that is why it is here).
  useEffect(() => {
    if (edit) return;
    let alive = true;
    loadBillableUploads(ctx.clientId)
      .then(rows => {
        if (!alive) return;
        if (upload && !rows.some(u => u.id === upload.id)) rows.unshift(upload);
        setOptions(rows);
      })
      .catch((e: any) => { if (alive) { setOptions(upload ? [upload] : []); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, edit]);   // eslint-disable-line react-hooks/exhaustive-deps
  const fill = async (id: string) => {
    setUploadId(id); setErr('');
    if (!id) return;
    setFilling(true);
    try {
      // The upload's own OCR fields, as stored — the server derives nothing and neither does this form.
      const d: AwPayableFromUpload = await api.awPayableFromUpload(ctx.clientId, id);
      setVendor(d.vendor_name || ''); setTaxId(d.vendor_tax_id || ''); setBillNo(d.bill_no || '');
      if (d.bill_date) setBillDate(d.bill_date);
      setTotal(d.total_amount === null || d.total_amount === undefined ? '' : String(d.total_amount));
      setVat(d.vat_amount === null || d.vat_amount === undefined ? '' : String(d.vat_amount));
      setWht(d.wht_amount === null || d.wht_amount === undefined ? '' : String(d.wht_amount));
    } catch (e: any) { setErr(perr(e, t)); } finally { setFilling(false); }
  };
  useEffect(() => { if (upload && !edit) fill(upload.id); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const nTotal = fin(total), nVat = fin(vat), nWht = fin(wht);
  const numsOk = nTotal !== null && !Number.isNaN(nTotal) && nTotal >= 0
    && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0))
    && (nWht === null || (!Number.isNaN(nWht) && nWht >= 0 && nWht <= nTotal));
  const whtOver = nTotal !== null && nWht !== null && !Number.isNaN(nTotal) && !Number.isNaN(nWht) && nWht > nTotal;
  // The server's rule exactly: only a LOWERING of the stored WHT below what was withheld is refused.
  const whtBelow = edit && !noteOnly && whtFloor > 0 && nWht !== null && !Number.isNaN(nWht)
    && (nWht || 0) < r2(Number(payable!.wht_amount) || 0) - 0.005 && (nWht || 0) < whtFloor - 0.005;
  // The register's own rule (to_pay = total − WHT), shown so the operator sees what the server will store.
  const toPay = numsOk ? r2((nTotal as number) - (nWht || 0)) : null;
  const curBad = !curOk(currency);
  const ready = noteOnly || (!!vendor.trim() && !!billNo.trim() && isoDate(billDate) && (!dueDate || isoDate(dueDate)) && numsOk
    && [vendor, taxId, billNo].every(okStr) && !curBad && !filling && !whtBelow);
  const vatLocked = edit && !noteOnly && !!payable?.tax_line_id && (!!settleLock?.entries?.length || !!unbookRef);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const body: AwPayableBody = {
      vendor_name: vendor.trim(), vendor_tax_id: taxId.trim(), bill_no: billNo.trim(), bill_date: billDate, due_date: dueDate,
      total_amount: r2(nTotal as number), vat_amount: r2(nVat || 0), wht_amount: r2(nWht || 0), note: noteTxt.trim(),
    };
    if (currency.trim()) body.currency = currency.trim().toUpperCase();
    // Sent whenever the operator opened the line editor at all (even to empty it); a bill whose
    // lines were never touched sends none and keeps what it had.
    if (!noteOnly && !vatLocked) body.lines = lines.filter(l => l.description.trim() || Number(l.unit_price)).map(l => ({
      description: l.description.trim(), qty: Number(l.qty) || 0, unit_price: Number(l.unit_price) || 0,
      doc_item_code: l.doc_item_code.trim(), charge_code: l.charge_code.trim(), account_code: l.account_code,
    }));
    if (!edit && uploadId) body.expense_upload_id = uploadId;
    // (Review 2026-11-03) A booked bill may post itself while this form stands open (services/autoPost.ts), and a refused Save pages the admins: asked again here, as the form asked
    // when it opened. Newly locked, the fields that reach the VAT line go back to what the bill holds, as they would have opened, and the form says so; nothing is sent, and a second Save takes the rest.
    if (edit && !noteOnly && !vatLocked && payable?.tax_line_id) {
      let now: any = null;
      try { now = await api.awPayable(ctx.clientId, payable.id); } catch { now = null; /* unanswered: the save itself answers */ }
      if (now?.settleLock?.entries?.length || now?.unbookRefusal) {
        setSettleLock(now.settleLock || null); setUnbookRef(now.unbookRefusal || null);
        setVendor(payable.vendor_name || ''); setTaxId(payable.vendor_tax_id || ''); setBillNo(payable.bill_no || ''); setBillDate(payable.bill_date || todayIso());
        setCurrency(payable.currency || ''); setTotal(String(payable.total_amount)); setVat(String(payable.vat_amount));
        setErr(t('aw_pay_now_locked')); setBusy(false);
        return;
      }
    }
    try {
      if (noteOnly) await api.awPayableUpdate(ctx.clientId, payable!.id, { note: noteTxt.trim() });
      else if (edit) await api.awPayableUpdate(ctx.clientId, payable!.id, body);
      else await api.awPayableCreate(ctx.clientId, body);
      onSaved();
    } catch (e: any) {
      // Raced (a payment recorded since the form read the floor): the refusal in the reader's language.
      const pl = e?.payload;
      setErr(pl?.code === 'wht_below_withheld' ? t('aw_pay_wht_below_withheld', { withheld: amt(Number(pl?.params?.withheld) || 0) }) : perr(e, t));
      setBusy(false);
    }
  };
  // ⛔ No docNo on a bill: the number is an EDITABLE field below, and a header still printing the
  // stored one while the operator retypes it would be the window arguing with itself.
  return (
    <Dialog title={edit ? t('aw_pay_edit_title') : t('aw_pay_new_title')} onClose={onClose} busy={busy} dirty={dirty} fullscreen
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={edit ? 'ti ti-device-floppy' : 'ti ti-plus'} label={edit ? t('aw_doc_save') : t('aw_pay_create_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!edit && (
          <div>
            <label style={lbl}>{t('aw_pay_from_upload')}</label>
            {options === null ? <Loader /> : (
              // Marked HERE and not inside fill(): the mount effect calls fill() too, for the upload
              // the operator arrived from, and that prefill is not something they typed.
              <select value={uploadId} onChange={e => { setDirty(true); fill(e.target.value); }} style={field} disabled={filling}>
                <option value="">{t('aw_pay_from_upload_none')}</option>
                {options.map(u => <option key={u.id} value={u.id}>{uploadLabel(u)}</option>)}
              </select>
            )}
            <div style={note}>{filling ? <><i className="ti ti-loader" aria-hidden /> {t('aw_pay_filling')}</> : t('aw_pay_from_upload_hint')}</div>
          </div>
        )}
        {edit && payable?.expense_filename && <div style={note}><i className="ti ti-paperclip" aria-hidden /> {t('aw_pay_linked_upload')}: {payable.expense_filename}</div>}
        {noteOnly && <div style={note}><i className="ti ti-lock" aria-hidden /> {t('aw_pay_note_only_hint')}</div>}
        {vatLocked && !settleLock?.entries?.length && unbookRef && (
          <div style={{ ...note, color: '#92400E' }} data-bill-vat-locked={unbookRef.code}><i className="ti ti-lock" aria-hidden /> {t('aw_pay_vat_locked_lead')} {unbookWhy(unbookRef, t)}</div>
        )}
        {vatLocked && !!settleLock?.entries?.length && settleLock && (() => {
          // (W3 ship review, S4) A settlement posted in a period closed since is never reversed: no "reverse it first".
          const shut = closedOf(settleLock.entries);
          return (
            <div style={{ ...note, color: '#92400E' }} data-bill-vat-locked={shut.length ? 'closed' : ''}><i className="ti ti-lock" aria-hidden /> {shut.length
              ? t('aw_bill_settle_lock_fields_closed', { docNo: shut.map(e => e.docNo || e.entryId.slice(0, 8)).join(', '), from: dmy(shut[0].closed!.start), to: dmy(shut[0].closed!.end) })
              : billLockText(t, settleLock.entries, true)}</div>
          );
        })()}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_vendor')}><input value={vendor} onChange={e => ed(setVendor, e.target.value)} maxLength={MAX_STR} disabled={noteOnly || vatLocked} style={field} /></Field>
          <Field label={t('aw_pay_vendor_tax_id')}><input value={taxId} onChange={e => ed(setTaxId, e.target.value)} maxLength={MAX_STR} disabled={noteOnly || vatLocked} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_bill_no')}><input value={billNo} onChange={e => ed(setBillNo, e.target.value)} maxLength={MAX_STR} disabled={noteOnly || vatLocked} style={field} /></Field>
          <Field label={t('aw_pay_bill_date')}><input type="date" value={billDate} onChange={e => ed(setBillDate, e.target.value)} disabled={noteOnly || vatLocked} style={field} /></Field>
          <Field label={t('aw_pay_due_date')}><input type="date" value={dueDate} onChange={e => ed(setDueDate, e.target.value)} disabled={noteOnly} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_col_currency')}>
            <input value={currency} onChange={e => ed(setCurrency, e.target.value)} maxLength={3} placeholder={t('aw_pay_currency_default')} disabled={noteOnly || vatLocked} style={field} />
            <div style={{ ...note, marginTop: 3, color: curBad ? '#B42318' : undefined }}>{t('aw_pay_currency_hint')}</div>
          </Field>
          <Field label={t('aw_pay_total')}><input type="number" step="0.01" min="0" value={total} onChange={e => ed(setTotal, e.target.value)} disabled={noteOnly || vatLocked} style={field} data-bill-total /></Field>
          <Field label={t('aw_pay_vat')}><input type="number" step="0.01" min="0" value={vat} onChange={e => ed(setVat, e.target.value)} disabled={noteOnly || vatLocked} style={field} data-bill-vat /></Field>
          <Field label={t('aw_pay_wht')}><input type="number" step="0.01" min="0" value={wht} onChange={e => ed(setWht, e.target.value)} disabled={noteOnly} style={field} data-bill-wht /></Field>
        </div>
        {whtOver && <div style={{ fontSize: 11.5, color: '#B42318' }}>{t('aw_pay_wht_over')}</div>}
        {edit && !noteOnly && whtFloor > 0 && (
          <div style={{ fontSize: 11.5, color: whtBelow ? '#B42318' : 'var(--text-tertiary)' }} data-wht-floor={whtFloor} data-wht-below={whtBelow ? 1 : 0}>
            {whtBelow ? t('aw_pay_wht_below_withheld', { withheld: amt(whtFloor) }) : t('aw_pay_wht_withheld_floor', { withheld: amt(whtFloor) })}
          </div>
        )}
        <div style={{ ...card, display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 13px' }}>
          <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-tertiary)' }}>{t('aw_pay_to_pay')}</span>
          <span style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{toPay === null ? '—' : amt(toPay)}</span>
          <span style={note}>{t('aw_pay_to_pay_rule')}</span>
        </div>
        {!noteOnly && !vatLocked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-tertiary)' }}>{t('aw_pay_lines')}</span>
              <span style={note}>{t('aw_pay_lines_hint')}</span>
            </div>
            {lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={l.description} placeholder={t('aw_po_line_description')} style={{ ...field, flex: '2 1 180px' }}
                  onChange={e => ed(setLines, ls => ls.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                <input type="number" step="any" min="0" value={l.qty} placeholder={t('aw_po_qty')} style={{ ...field, width: 70 }}
                  onChange={e => ed(setLines, ls => ls.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} />
                <input type="number" step="0.01" min="0" value={l.unit_price} placeholder={t('aw_po_unit_price')} style={{ ...field, width: 110 }}
                  onChange={e => ed(setLines, ls => ls.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
                <input value={l.charge_code} placeholder={t('aw_pay_line_charge')} title={t('aw_pay_line_charge_title')} style={{ ...field, width: 130 }}
                  onChange={e => ed(setLines, ls => ls.map((x, j) => j === i ? { ...x, charge_code: e.target.value } : x))} />
                <select value={l.account_code} title={t('aw_pay_line_account')} style={{ ...field, width: 210 }}
                  onChange={e => ed(setLines, ls => ls.map((x, j) => j === i ? { ...x, account_code: e.target.value } : x))}>
                  <option value="">{t('aw_pay_line_account_none')}</option>
                  {accounts.map(a2 => <option key={a2.code} value={a2.code}>{a2.code} · {a2.name_th || a2.name_en}</option>)}
                </select>
                <button type="button" style={{ ...btn, padding: '4px 9px' }} title={t('aw_po_remove_line')}
                  onClick={() => ed(setLines, ls => ls.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" style={{ ...btn, padding: '4px 10px', fontSize: 12 }} onClick={() => ed(setLines, ls => [...ls, blank()])}>
                <i className="ti ti-plus" aria-hidden /> {t('aw_pay_line_add')}
              </button>
              {/* The accounts a person picks here only reach the books when the lines add up to the
                  bill without its VAT: the posting engine splits by item ONLY on an exact tie and
                  otherwise puts the whole document on one account and says so. Shown rather than
                  enforced — a bill may legitimately be described in part — but never silently. */}
              {!!lines.length && (
                <span style={{ fontSize: 11.5, color: linesTie ? 'var(--text-tertiary)' : '#B45309' }}>
                  {linesTie
                    ? t('aw_pay_lines_tie', { sum: amt(lineSum) })
                    : t('aw_pay_lines_untied', { sum: amt(lineSum), base: amt(netBase) })}
                </span>
              )}
            </div>
          </div>
        )}
        <Field label={t('aw_bank_note_opt')}><textarea value={noteTxt} onChange={e => ed(setNoteTxt, e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></Field>
        <div style={note}>{t('aw_pay_entered_note')}</div>
        {/* (Owner 2026-11-02) Once it is saved a bill takes files here too. Nothing typed above is sent by this: an upload stands by itself. */}
        {edit && !!payable && <RecordEvidence clientId={ctx.clientId} kind="payable" id={payable.id} />}
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Record payment dialog (batch 11 R5: the payment register v2 — design §3.2, the W2 brief §3 P5 task 13) ────────
// ONE payment over one or more bills, full screen: its date, method and bank account; a row per bill (the cash paid
// now, and the WHT taken off that bill — created as a new PND line, or a hand-entered line claimed); the statement
// line it came from, now or later; its slips. Written through services/expensePayments.ts in one request
// (POST /expense-payments), the files after it. ⛔ Every refusal the server could make is asked FIRST and answered by a
// 200 (options, wht-candidates, lines): Save stays disabled until every check the server runs is met here — the gross
// is levelled ±0.01 so `wht_figures` is never sent, a bill with WHT needs the Tax module, the date's closed period,
// a company with no bank account — and while the request is in flight (a second click would record a second payment).
// Nothing here posts: a payment is never a posting source (design D1); its statement line is.
type PayBillRef = Pick<AwPayable, 'id'> & Partial<AwPayable>;
export type PayRow = {
  cash: string; wht: string; whtTouched: boolean; incomeType: string; payeeType: '' | 'company' | 'individual';
  rate: string; gross: string; grossTouched: boolean; mode: '' | 'new' | 'claim'; claimLineId: string; another: boolean;
};
export const whtOf = (gross: number, rate: number): number => Math.round((Number(gross) || 0) * (Number(rate) || 0) / 100 * 100) / 100;
/** The WHT the server pre-fills for `cash` (design §3.2.3): the proportional share, or — paying the bill off — the
 *  remainder the server computed for exactly that (options' whtDefault). */
export const whtFor = (b: EpayOptionBill, cash: number): number => {
  if (!(b.whtFace > 0) || !(b.toPay > 0) || !(cash > 0)) return 0;
  return cash >= b.outstanding - 0.005 ? b.whtDefault : r2(b.whtFace * cash / b.toPay);
};
/** A gross that gives exactly `wht` at `rate` — round2(w × 100 / rate), moved by a satang or two when the rounding
 *  needs it (the server's own check, whtAmount(gross, rate) = w). null when none is that close. */
export const levelGross = (wht: number, rate: number): number | null => {
  if (!(wht > 0) || !(rate > 0)) return null;
  const g0 = r2(wht * 100 / rate);
  for (const d of [0, -0.01, 0.01, -0.02, 0.02, -0.03, 0.03]) { const g = r2(g0 + d); if (whtOf(g, rate) === wht) return g; }
  return null;
};
/** A refusal of the register, worded by its code (aw_epay_err_<code>); anything else as the pane always worded it. */
const epayErr = (e: any, t: TF): string => {
  const code = String(e?.payload?.code || '');
  return (EPAY_ERRORS as readonly string[]).includes(code) ? t(`aw_epay_err_${code}`) : perr(e, t);
};
const period = (y: number, m: number) => `${String(m).padStart(2, '0')}/${y}`;
/** The row a bill opens with: the cash (what it owes, or the line's amount when one bill pays one line), the WHT that
 *  cash takes by default, the payee type the tax id says. None of it is an edit. */
const openRow = (b: EpayOptionBill, cash: number): PayRow => {
  const w = whtFor(b, cash);
  return { cash: String(r2(cash)), wht: String(w), whtTouched: false, incomeType: '', payeeType: b.payeeTypeGuess, rate: '', gross: '', grossTouched: false,
    mode: '', claimLineId: '', another: false };
};
/** The row after its cash changed: the WHT follows the default until a person typed one, the gross follows the WHT. */
const withCash = (b: EpayOptionBill, r: PayRow, v: string): Partial<PayRow> => {
  if (r.whtTouched) return { cash: v };
  const w = whtFor(b, Number(v) || 0);
  const g = !r.grossTouched ? levelGross(w, Number(r.rate)) : null;
  return { cash: v, wht: String(w), ...(g !== null ? { gross: String(g) } : {}) };
};
export const withWht = (r: PayRow, v: string): Partial<PayRow> => {
  const g = !r.grossTouched ? levelGross(Number(v) || 0, Number(r.rate)) : null;
  return { wht: v, whtTouched: true, ...(g !== null ? { gross: String(g) } : {}) };
};
export const withIncome = (r: PayRow, label: string, types: { label: string; rate: number }[]): Partial<PayRow> => {
  const rate = types.find(x => x.label === label)?.rate ?? (Number(r.rate) || 0);
  const g = !r.grossTouched ? levelGross(Number(r.wht) || 0, rate) : null;
  return { incomeType: label, rate: String(rate), ...(g !== null ? { gross: String(g) } : {}) };
};
export const withRate = (r: PayRow, v: string): Partial<PayRow> => {
  const g = !r.grossTouched ? levelGross(Number(r.wht) || 0, Number(v) || 0) : null;
  return { rate: v, ...(g !== null ? { gross: String(g) } : {}) };
};
/** What a bill's row still lacks before the server would take it ('' = ready), checked as the server checks it.
 *  `takenBy`: the bill number of an EARLIER row of this payment that claims the same hand WHT line — the server takes a
 *  line once (409 wht_claim_taken), so the second claim is refused here, in words (W2 ship, the money review). */
export function rowWhy(b: EpayOptionBill, r: PayRow | undefined, c: EpayWhtCandidates | undefined, canWht: boolean, t: TF, takenBy = ''): string {
  if (!r) return t('aw_pay_filling');
  if (b.refusal) return t(`aw_epay_err_${b.refusal.code}`);
  const cash = Number(r.cash);
  if (!Number.isFinite(cash) || !(cash > 0)) return t('aw_epay_err_amount_zero');
  if (r2(cash) > r2(b.outstanding)) return t('aw_pay_amount_over', { max: amt(b.outstanding) });
  if (!(b.whtFace > 0)) return '';
  if (!canWht) return t('aw_epay_wht_needs_tax');
  const w = Number(r.wht);
  if (!Number.isFinite(w) || w < 0) return t('aw_epay_err_invalid');
  // The server's ceiling (W2 ship): the new allocations' WHT and the share counted for the payments made before it.
  const used = r2(b.whtTaken + (b.whtEarlier || 0));
  if (r2(used + w) > r2(b.whtFace)) return t('aw_epay_wht_left', { left: amt(Math.max(0, r2(b.whtFace - used))) });
  if (!(w > 0)) return '';
  if (!c) return t('aw_pay_filling');
  const claimable = c.rows.filter(x => x.claimable);
  if (r.mode === 'claim') {
    const hit = claimable.find(x => x.id === r.claimLineId);
    if (!hit) return t('aw_epay_err_wht_claim_mismatch');
    if (takenBy) return t('aw_epay_wht_claim_twice', { bill: takenBy });
    return r2(hit.wht) === r2(w) ? '' : t('aw_epay_wht_line_differs', { wht: amt(hit.wht) });
  }
  if (!c.canCreate) return t('aw_epay_err_wht_month_filed');
  if (c.anotherRequired && !(r.mode === 'new' && r.another)) return t('aw_epay_err_wht_another_required');
  if (!r.incomeType) return t('aw_epay_err_wht_income_type_required');
  if (!r.payeeType) return t('aw_epay_err_wht_payee_type_required');
  const rate = Number(r.rate), gross = Number(r.gross);
  if (!(rate > 0) || !(gross > 0) || whtOf(gross, rate) !== r2(w)) return t('aw_epay_wht_figures_off', { gross: amt(gross || 0), rate: rate || 0, calc: amt(whtOf(gross || 0, rate || 0)), wht: amt(w) });
  return '';
}
/** The file a payment may keep, or why not (the server's own three rules — type, 5 MB, ten a payment). */
const fileWhy = (f: File, t: TF): string => !(EPAY_FILE_TYPES as readonly string[]).includes(f.type) ? t('aw_epay_file_bad_type', { name: f.name })
  : f.size > EPAY_FILE_MAX ? t('aw_epay_file_too_big', { name: f.name }) : '';

/** The bills a payment from a statement line may pay (Banking's "Pay several bills from this line"): this company's
 *  open and partially paid bills, searched, 100 a page. A list, not a form — choosing one adds it to the payment. */
function BillPicker({ clientId, exclude, onPick }: { clientId: string; exclude: string[]; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const [all, setAll] = useState<AwPayable[] | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => {
    let alive = true;
    loadAllPayables(clientId).then(rows => { if (alive) setAll(rows.filter(isLive)); }).catch(() => { if (alive) setAll([]); });
    return () => { alive = false; };
  }, [clientId]);
  const k = q.trim().toLowerCase();
  const rows = (all || []).filter(p => !exclude.includes(p.id) && (!k || [p.bill_no, p.vendor_name, p.vendor_tax_id].some(v => String(v || '').toLowerCase().includes(k))));
  const shown = rows.slice((page - 1) * 100, page * 100);
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }} data-epay-picker>
      <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder={t('aw_pay_search_ph')} aria-label={t('aw_epay_bill_pick')} style={field} />
      {all === null ? <Loader /> : !shown.length ? <div style={note}>{t('aw_epay_bill_pick_none')}</div> : (
        <table style={table}><tbody>
          {shown.map(p => (
            <tr key={p.id}>
              <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{p.bill_no}</td><td style={td}>{p.vendor_name}</td>
              <td style={num}>{amt(p.outstanding)} {p.currency}</td>
              <td style={{ ...td, textAlign: 'right' }}><button type="button" onClick={() => onPick(p.id)} style={actBtn}><i className="ti ti-plus" aria-hidden /> {t('aw_epay_bill_pick')}</button></td>
            </tr>
          ))}
        </tbody></table>
      )}
      {rows.length > 100 && <Pagination page={page} total={rows.length} limit={100} onPage={setPage} />}
    </div>
  );
}

export function PaymentDialog({ ctx, bills, line, onClose, onDone }: { ctx: AwCtx; bills: PayBillRef[]; line?: AwBankLine; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const lineAmt = line ? r2(Number(line.debit) || 0) : 0;
  const [payDate, setPayDate] = useState(line ? line.txn_date : todayIso());
  const [method, setMethod] = useState<EpayMethod>('transfer');
  const [accountId, setAccountId] = useState(line ? line.bank_account_id : '');
  const [ids, setIds] = useState<string[]>(bills.map(b => b.id));
  const [rows, setRows] = useState<Record<string, PayRow>>({});
  const [opts, setOpts] = useState<EpayOptions | null>(null);
  const [cands, setCands] = useState<Record<string, EpayWhtCandidates>>({});
  const [lines, setLines] = useState<AwBankLine[] | null>(null);
  const [lineId, setLineId] = useState(line ? line.id : '');
  // (Batch 11 W4 R7, P7b task 3) Cash: the petty cash box it comes out of — then no bank account and no statement line.
  const [boxId, setBoxId] = useState('');
  const cash = method === 'cash';
  const [files, setFiles] = useState<File[]>([]);
  const [fileErr, setFileErr] = useState('');
  const [reference, setReference] = useState('');
  const [noteTxt, setNoteTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recorded, setRecorded] = useState('');
  // FS-12 — see BillDialog. Every figure this window opens with is given (the bills' outstanding, their default WHT,
  // the line's date and account), so opening it and closing it again asks nothing.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  // ONE funnel for a bill's line of this payment: every edit of it marks the form.
  const setLine = (id: string, patch: Partial<PayRow>) => { setDirty(true); setRows(p => ({ ...p, [id]: { ...p[id], ...patch } })); };
  const pickLine = (id: string) => { setDirty(true); setLineId(id); };
  const inflight = React.useRef(false);   // a second click lands before React re-renders `busy`
  const dateKey = isoDate(payDate) ? payDate : '';
  useEffect(() => {
    if (!dateKey) return;
    let alive = true;
    epayApi.options(ctx.clientId, ids, dateKey).then(o => { if (alive) { setOpts(o); setErr(''); } }).catch((e: any) => { if (alive) setErr(epayErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ids.join(','), dateKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!opts) return;
    // A bill new to this window opens on what it owes (one bill paying one line: at most the line); the account is the
    // line's, else the company's only one. Given, not typed.
    setRows(prev => {
      const next = { ...prev };
      for (const b of opts.bills) if (!next[b.id]) next[b.id] = openRow(b, line && opts.bills.length === 1 ? Math.min(b.outstanding, lineAmt) : b.outstanding);
      return next;
    });
    // The one account the payment can come out of is chosen for the person; one in another currency never is (W2 ship).
    const usable = opts.bankAccounts.filter(a => !a.foreign);
    setAccountId(cur => cur || (usable.length === 1 ? usable[0].id : ''));
  }, [opts]);   // eslint-disable-line react-hooks/exhaustive-deps
  const withWhtBills = (opts?.bills || []).filter(b => b.whtFace > 0 && !b.refusal && ids.includes(b.id));
  const candKey = withWhtBills.map(b => `${b.id}:${rows[b.id]?.payeeType || b.payeeTypeGuess || 'company'}`).join(',');
  useEffect(() => {
    if (!opts?.canWht || !dateKey || !candKey) return;
    let alive = true;
    const asks = candKey.split(',').map(part => part.split(':'));
    Promise.all(asks.map(([id, pt]) => epayApi.whtCandidates(ctx.clientId, id, dateKey, pt).then(c => [id, c] as const).catch(() => null)))
      .then(got => { if (alive) setCands(p => ({ ...p, ...Object.fromEntries(got.filter((x): x is readonly [string, EpayWhtCandidates] => !!x)) })); });
    return () => { alive = false; };
  }, [ctx.clientId, dateKey, candKey, opts?.canWht]);   // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (opts?.bills || []).filter(b => ids.includes(b.id));
  const totalCash = r2(shown.reduce((s, b) => s + (Number(rows[b.id]?.cash) || 0), 0));
  const totalWht = r2(shown.reduce((s, b) => s + (b.whtFace > 0 ? (Number(rows[b.id]?.wht) || 0) : 0), 0));
  useEffect(() => {
    if (line || cash || !accountId || !(totalCash > 0) || !dateKey) { setLines(line ? null : []); if (cash) setLineId(''); return; }
    let alive = true;
    // The picker lists only what the attach door takes: this account's unmatched debits of exactly this amount.
    epayApi.lines(ctx.clientId, accountId, totalCash, dateKey).then(d => {
      if (!alive) return;
      setLines(d.rows || []);
      setLineId(cur => ((d.rows || []).some(l => l.id === cur) ? cur : ''));
    }).catch(() => { if (alive) setLines([]); });
    return () => { alive = false; };
  }, [ctx.clientId, accountId, totalCash, dateKey, line, cash]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The boxes Cash may come out of on this pay date: an active box opened on or before it (the server's own before_opening
  // rule) — a box opened later is offered greyed with its date, never chosen; with no active box Cash is offered disabled
  // with the server's sentence (`cashRefusal`); on a dialog carrying a statement line Cash is not offered at all (a cash
  // payment has no line — 400 invalid).
  const boxes = opts?.boxes || [];
  const boxOpen = (b: { openingDate: string }) => !!dateKey && b.openingDate <= dateKey;
  const cashBlocked = !!line || !boxes.length || !!opts?.cashRefusal;
  const box = boxes.find(b => b.id === boxId && boxOpen(b)) || null;
  useEffect(() => {
    // The one box the payment can come out of is chosen for the person; a box no longer usable on this date is let go.
    if (!cash) return;
    const usable = boxes.filter(boxOpen);
    setBoxId(cur => (cur && usable.some(b => b.id === cur)) ? cur : (usable.length === 1 ? usable[0].id : ''));
  }, [cash, opts, dateKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const takeFiles = (list: FileList | null) => {
    const add = Array.from(list || []);
    const bad = add.map(f => fileWhy(f, t)).filter(Boolean);
    setFileErr(bad[0] || (files.length + add.length > EPAY_FILES_PER_PAYMENT ? t('aw_epay_files_max') : ''));
    if (!bad.length && files.length + add.length <= EPAY_FILES_PER_PAYMENT) setFiles(p => [...p, ...add]);
  };
  const canWht = !!opts?.canWht;
  // The hand WHT lines this payment's rows claim, each by the FIRST row that claims it (rows in the order shown): a later
  // row claiming one of them is refused (rowWhy's takenBy), and the line is offered to no other row (the radio below).
  const claimOf = (b: EpayOptionBill): string => {
    const r = rows[b.id];
    return b.whtFace > 0 && !b.refusal && r?.mode === 'claim' && !!r.claimLineId && (Number(r.wht) || 0) > 0 ? r.claimLineId : '';
  };
  const firstClaim = new Map<string, EpayOptionBill>();
  for (const b of shown) { const l = claimOf(b); if (l && !firstClaim.has(l)) firstClaim.set(l, b); }
  const whys = shown.map(b => {
    const l = claimOf(b), first = l ? firstClaim.get(l) : undefined;
    return rowWhy(b, rows[b.id], cands[b.id], canWht, t, first && first.id !== b.id ? first.billNo : '');
  });
  const lineWhy = line && r2(totalCash) !== lineAmt ? t('aw_epay_line_differs', { line: amt(lineAmt), cash: amt(totalCash) }) : '';
  const ready = !!opts && !recorded && shown.length > 0 && isoDate(payDate) && !opts.payDateRefusal && (EPAY_METHODS as readonly string[]).includes(method)
    && (cash ? !cashBlocked && !!box : !!accountId && opts.bankAccounts.some(a => a.id === accountId && !a.foreign))
    && whys.every(w => !w) && !lineWhy && okStr(reference) && okStr(noteTxt);
  const submit = async () => {
    if (!ready || busy || inflight.current || !opts) return;
    inflight.current = true; setBusy(true); setErr('');
    const allocations = shown.map(b => {
      const r = rows[b.id], w = b.whtFace > 0 ? r2(Number(r.wht) || 0) : 0;
      return { payableId: b.id, cash: r2(Number(r.cash)), wht: !(w > 0) ? null : r.mode === 'claim'
        ? { amount: w, mode: 'claim' as const, claimLineId: r.claimLineId }
        : { amount: w, mode: 'new' as const, incomeType: r.incomeType, payeeType: r.payeeType || undefined, rate: Number(r.rate), gross: r2(Number(r.gross)), another: r.another } };
    });
    let made: EpayRecorded;
    try {
      made = await epayApi.record(ctx.clientId, cash
        ? { payDate, method, cashBoxId: boxId, reference: reference.trim() || undefined, note: noteTxt.trim() || undefined, allocations }
        : { payDate, method, bankAccountId: accountId, bankLineId: lineId || undefined, reference: reference.trim() || undefined, note: noteTxt.trim() || undefined, allocations });
    } catch (e: any) { setErr(epayErr(e, t)); setBusy(false); inflight.current = false; return; }
    if (files.length) {
      try { await epayApi.addFiles(ctx.clientId, made.id, files); }
      catch (e: any) { setRecorded(made.payNo); setErr(t('aw_epay_files_failed', { payNo: made.payNo, why: epayErr(e, t) })); setBusy(false); return; }
    }
    onDone();
  };
  const footer = recorded
    ? <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="button" onClick={onDone} style={btnPrimary}><i className="ti ti-check" aria-hidden /> {t('aw_epay_done')}</button></div>
    : <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-cash" label={t('aw_pay_record_btn')} t={t} />;
  return (
    <Dialog fullscreen title={1 < shown.length ? t('aw_epay_title_bills', { n: shown.length }) : t('aw_epay_title')} docNo={shown.length === 1 ? shown[0].billNo : undefined}
      onClose={onClose} busy={busy} dirty={dirty} footer={footer}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-epay-dialog>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <Field label={t('aw_pay_paid_date')}><input type="date" value={payDate} onChange={e => ed(setPayDate, e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_method')}>
            <select value={method} onChange={e => ed(setMethod, e.target.value as EpayMethod)} style={field} data-epay-method>
              {/* (Batch 11 W4 R7) Cash: out of a petty cash box. Not offered on a dialog carrying a statement line (a cash payment
                  has none); disabled, with the server's sentence below, while the client has no active box. */}
              {EPAY_METHODS.filter(m => m !== 'cash' || !line).map(m => (
                <option key={m} value={m} disabled={m === 'cash' && cashBlocked} data-epay-method-option={m}>{t(`aw_pay_method_${m}`)}</option>
              ))}
            </select>
            {!line && !!opts && cashBlocked && <div style={note} data-epay-cash-refusal>{t('aw_epay_cash_refusal')}</div>}
          </Field>
          {cash ? (
            <Field label={t('aw_epay_box')}>
              <select value={boxId} onChange={e => ed(setBoxId, e.target.value)} style={field} data-epay-box>
                <option value="">{t('aw_epay_box_pick')}</option>
                {boxes.map(b => (
                  <option key={b.id} value={b.id} disabled={!boxOpen(b)} data-epay-box-option={b.id} data-epay-box-closed={boxOpen(b) ? undefined : 1}>
                    {boxOpen(b) ? t('aw_epay_box_option', { name: b.name, balance: `${b.balanceSat < 0 ? '−' : ''}${baht(Math.abs(b.balanceSat))}` })
                      : t('aw_epay_box_opens', { name: b.name, date: dmy(b.openingDate) })}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label={t('aw_epay_account')}>
              <select value={accountId} onChange={e => ed(setAccountId, e.target.value)} disabled={!!line} style={field} data-epay-account>
                {!line && <option value="">{t('aw_epay_account_pick')}</option>}
                {(opts?.bankAccounts || []).map(a => <option key={a.id} value={a.id} disabled={!!a.foreign} data-epay-account-foreign={a.foreign ? 1 : undefined}>{a.label}{a.paired ? ` · ${t('aw_epay_account_paired')}` : ''}{a.foreign ? ` · ${t('aw_epay_account_foreign', { currency: a.currency })}` : ''}</option>)}
              </select>
            </Field>
          )}
        </div>
        {!cash && opts && !opts.bankAccounts.length && <div style={errBox} data-epay-why="bank_account_required">{t('aw_epay_no_account')}</div>}
        {opts?.payDateRefusal && <div style={errBox} data-epay-why="period_closed">{t('aw_epay_err_period_closed')}</div>}
        {!cash && !!opts?.bankAccounts.find(a => a.id === accountId && a.foreign) && <div style={errBox} data-epay-why="currency">{t('aw_epay_err_currency')}</div>}
        {cash && !!box && (() => {
          const after = box.balanceSat - Math.round(totalCash * 100);
          return (
            <div style={{ fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-epay-balance-after={after}>
              <span>{t('aw_epay_balance_after', { amount: `${after < 0 ? '−' : ''}${baht(Math.abs(after))}` })}</span>
              {/* D25: a negative box is said, never refused — the books are right, a top-up is missing. */}
              {after < 0 && <span style={pillAmber}>{t('aw_pc_negative')}</span>}
            </div>
          );
        })()}
        {cash && !!boxes.length && !boxes.some(boxOpen) && <div style={{ ...note, color: '#92400E' }} data-epay-why="before_opening">{t('aw_epay_err_before_opening')}</div>}
        <div>
          <div style={sectionHead}>{t('aw_epay_bills')}</div>
          {!opts ? <Loader /> : !shown.length ? <div style={{ ...card, ...note }}>{t('aw_epay_bills_none')}</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {shown.map((b, i) => {
                const r = rows[b.id];
                const c = cands[b.id];
                const claimable = (c?.rows || []).filter(x => x.claimable);
                const w = Number(r?.wht) || 0;
                return (
                  <div key={b.id} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }} data-epay-bill={b.id}>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
                      <b>{b.billNo}</b>{!!b.expenseNo && <span data-epay-bill-exp={b.id} title={t('aw_er_col_no')} style={{ color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{b.expenseNo}</span>}<span style={{ flex: '1 1 160px' }}>{b.vendor}{b.vendorTaxId ? <span style={{ color: 'var(--text-tertiary)' }}> · {b.vendorTaxId}</span> : null}</span>
                      <span>{t('aw_epay_owes', { amount: `${amt(b.outstanding)} ${b.currency}` })}</span>
                      {shown.length > 1 && <button type="button" onClick={() => { setDirty(true); setIds(p => p.filter(x => x !== b.id)); }} style={actBtn} title={t('aw_epay_bill_remove')} aria-label={t('aw_epay_bill_remove')}><i className="ti ti-x" aria-hidden /></button>}
                    </div>
                    {b.refusal ? <div style={errBox}>{t(`aw_epay_err_${b.refusal.code}`)}</div> : r && (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                          <Field label={t('aw_epay_cash')}><input type="number" step="0.01" min="0" value={r.cash} onChange={e => setLine(b.id, withCash(b, r, e.target.value))} style={field} data-epay-cash={i} /></Field>
                        </div>
                        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }} data-epay-wht={b.id}>
                          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{t('aw_epay_wht_title')}</div>
                          {!(b.whtFace > 0) ? <div style={note}>{t('aw_epay_wht_none')}</div> : !canWht ? (
                            <div style={{ ...note, color: '#92400E' }} data-epay-why="tax_module">{t('aw_epay_wht_face', { face: amt(b.whtFace), taken: amt(b.whtTaken) })} — {t('aw_epay_wht_needs_tax')}</div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                              <div style={note}>{t('aw_epay_wht_face', { face: amt(b.whtFace), taken: amt(b.whtTaken) })}</div>
                              {(b.whtEarlier || 0) > 0 && <div style={note} data-epay-wht-earlier={b.id}>{t('aw_epay_wht_earlier', { earlier: amt(b.whtEarlier) })}</div>}
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                                <Field label={t('aw_epay_wht_amount')}><input type="number" step="0.01" min="0" value={r.wht} onChange={e => setLine(b.id, withWht(r, e.target.value))} style={field} data-epay-wht-amount={i} /></Field>
                                <Field label={t('aw_epay_wht_payee')}>
                                  <select value={r.payeeType} onChange={e => setLine(b.id, { payeeType: e.target.value as PayRow['payeeType'] })} style={field}>
                                    {!r.payeeType && <option value="">{t('aw_epay_wht_payee_pick')}</option>}
                                    <option value="company">{t('aw_epay_wht_company')}</option><option value="individual">{t('aw_epay_wht_individual')}</option>
                                  </select>
                                </Field>
                              </div>
                              {w > 0 && !!c && (claimable.length > 0 || c.anotherRequired) && (
                                <div style={{ ...card, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }} data-epay-claims={b.id}>
                                  <div style={{ fontSize: 12.5 }}>{t('aw_epay_wht_choose', { period: period(Number(payDate.slice(0, 4)), Number(payDate.slice(5, 7))) })}</div>
                                  {c.rows.map(x => {
                                    // Claimed by ANOTHER row of this payment (and not by this one): not offered here.
                                    const other = firstClaim.get(x.id);
                                    const elsewhere = !!other && other.id !== b.id && !(r.mode === 'claim' && r.claimLineId === x.id);
                                    return (
                                    <div key={x.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }} data-epay-claim-line={x.id}>
                                      {x.claimable
                                        ? <input type="radio" name={`claim-${b.id}`} disabled={elsewhere} checked={r.mode === 'claim' && r.claimLineId === x.id} onChange={() => setLine(b.id, { mode: 'claim', claimLineId: x.id, another: false })} aria-label={t('aw_epay_wht_claim')} />
                                        : <i className="ti ti-lock" style={{ color: 'var(--text-tertiary)' }} aria-hidden />}
                                      <span>{x.claimable ? t('aw_epay_wht_claim') : ''} {x.payeeName} · {amt(x.gross)} × {x.rate}% = <b>{amt(x.wht)}</b></span>
                                      {x.filed && <span style={pillGrey}>{t('aw_epay_wht_line_filed')}</span>}
                                      {x.role === 'released' && <span style={pillAmber}>{t('aw_epay_wht_line_released')}</span>}
                                      {!x.claimable && <span style={note}>{t('aw_epay_wht_line_locked')}</span>}
                                      {x.claimable && r.mode === 'claim' && r.claimLineId === x.id && r2(x.wht) !== r2(w) && (
                                        <button type="button" onClick={() => setLine(b.id, { wht: String(x.wht), whtTouched: true })} style={actBtn}>{t('aw_epay_wht_use_line', { wht: amt(x.wht) })}</button>
                                      )}
                                      {x.claimable && elsewhere && <span style={note} data-epay-claim-elsewhere={x.id}>{t('aw_epay_wht_claim_here', { bill: other!.billNo })}</span>}
                                    </div>
                                    );
                                  })}
                                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, opacity: c.canCreate ? 1 : 0.5 }}>
                                    <input type="radio" name={`claim-${b.id}`} disabled={!c.canCreate} checked={r.mode === 'new' && r.another} onChange={() => setLine(b.id, { mode: 'new', claimLineId: '', another: true })} />
                                    {t('aw_epay_wht_another')}
                                  </label>
                                </div>
                              )}
                              {w > 0 && !!c && !c.canCreate && <div style={{ ...note, color: '#92400E' }} data-epay-why="wht_month_filed">{t('aw_epay_wht_month_filed', { period: period(Number(payDate.slice(0, 4)), Number(payDate.slice(5, 7))) })}</div>}
                              {w > 0 && r.mode !== 'claim' && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                                  <Field label={t('aw_epay_wht_income')}>
                                    <select value={r.incomeType} onChange={e => setLine(b.id, withIncome(r, e.target.value, opts.incomeTypes))} style={field}>
                                      <option value="">{t('aw_epay_wht_income_pick')}</option>
                                      {opts.incomeTypes.map(x => <option key={x.label} value={x.label}>{x.label} ({x.rate}%)</option>)}
                                    </select>
                                  </Field>
                                  <Field label={t('aw_epay_wht_rate')}><input type="number" step="0.01" min="0" value={r.rate} onChange={e => setLine(b.id, withRate(r, e.target.value))} style={field} /></Field>
                                  <Field label={t('aw_epay_wht_gross')}>
                                    <input type="number" step="0.01" min="0" value={r.gross} onChange={e => setLine(b.id, { gross: e.target.value, grossTouched: true })}
                                      onBlur={() => { const g = levelGross(w, Number(r.rate)); if (g !== null && whtOf(Number(r.gross), Number(r.rate)) !== r2(w) && Math.abs(g - Number(r.gross)) <= 0.03) setLine(b.id, { gross: String(g) }); }}
                                      style={field} data-epay-gross={i} />
                                  </Field>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {!!whys[i] && <div style={{ fontSize: 11.5, color: '#B42318' }} data-epay-row-why={i}>{whys[i]}</div>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {line && <div style={{ marginTop: 8 }}><BillPicker clientId={ctx.clientId} exclude={ids} onPick={id => { setDirty(true); setIds(p => [...p, id]); }} /></div>}
        </div>
        {cash ? <div style={note} data-epay-cash-no-line>{t('aw_epay_cash_no_line')}</div> : <div>
          <div style={sectionHead}>{t('aw_epay_line')}</div>
          {line ? (
            <div style={{ ...card, fontSize: 12.5 }} data-epay-line-fixed>{t('aw_epay_line_fixed', { date: dmy(line.txn_date), amount: amt(lineAmt) })} · {line.description}
              {lineWhy && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 4 }}>{lineWhy}</div>}
            </div>
          ) : lines === null ? <Loader /> : (
            <>
              <select value={lineId} onChange={e => pickLine(e.target.value)} style={field} data-epay-lines>
                <option value="">{t('aw_epay_line_wait')}</option>
                {lines.map(l => <option key={l.id} value={l.id}>{dmy(l.txn_date)} · {l.account_number} · {l.description} · {amt(l.debit)}</option>)}
              </select>
              <div style={note}>{t('aw_epay_line_hint')}</div>
            </>
          )}
        </div>}
        <div>
          <div style={sectionHead}>{t('aw_epay_files')}</div>
          <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                <i className="ti ti-paperclip" aria-hidden /> <span style={{ flex: 1 }}>{f.name}</span>
                <button type="button" onClick={() => { setDirty(true); setFiles(p => p.filter((_, j) => j !== i)); }} style={actBtn} title={t('aw_epay_file_remove')} aria-label={t('aw_epay_file_remove')}><i className="ti ti-x" aria-hidden /></button>
              </div>
            ))}
            <label style={{ ...actBtn, alignSelf: 'flex-start', cursor: 'pointer' }}>
              <i className="ti ti-upload" aria-hidden /> {t('aw_epay_files_add')}
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                onChange={e => { setDirty(true); takeFiles(e.target.files); e.target.value = ''; }} data-epay-files />
            </label>
            <div style={note}>{t('aw_epay_files_hint')}</div>
            {fileErr && <div style={{ fontSize: 11.5, color: '#B42318' }} data-epay-why="file">{fileErr}</div>}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_reference')}><input value={reference} onChange={e => ed(setReference, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_bank_note_opt')}><input value={noteTxt} onChange={e => ed(setNoteTxt, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
        </div>
        <div style={{ ...card, fontWeight: 700, fontSize: 13 }} data-epay-totals>{t('aw_epay_totals', { cash: amt(totalCash), wht: amt(totalWht) })}</div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── New / edit debit / credit note — full screen in both modes (FS-6, batch 12 tail T1) ──────────────────────────────
// New: the bill picked (or the one handed in), every field typed. Edit (`note`): the same form PRE-FILLED from the note,
// the bill a fixed card (a note stays on its bill, TL-5), and only what the server's verdict allows — read first from
// GET /purchase-notes/:nid (the window's pre-check, `edit` and `limits`): a booked note's figures are facts and its
// reason the only input; a void bill's note is never offered an Edit. The credit cap and the floor are held LIVE
// (noteEditBreach — the door's own rule), so Save is disabled with the sentence beside it and a refused PATCH is never
// sent. Save sends ONE PATCH with the changed fields only; the door asks the verdict again inside its transaction.
/** The edit door's codes, in its order — services/purchaseNotes.ts NOTE_EDIT_CODES (testSettleI18n holds the mirror). */
const NOTE_EDIT_CODES = ['not_found', 'bill_void', 'note_booked', 'credit_cap', 'outstanding_negative', 'invalid'];
const NOTE_EPS = 0.005;
/** The edit door's limits, live (services/purchaseNotes.ts limitBreach): the credit cap only where the credit GROWS, the
 *  floor only where the bill's outstanding falls below 0 AND below where it stood (TL-4) — never a refusal for a
 *  number, date, VAT or reason. `own` is the note as it stands; `limits` the window's pre-check. */
export function noteEditBreach(own: { kind: string; amount: number }, limits: { creditCap: number; outstandingBefore: number }, kind: string, amount: number):
  { code: 'credit_cap'; max: number } | { code: 'outstanding_negative'; after: number } | null {
  const ownAmt = r2(Number(own.amount) || 0), a = r2(Number(amount) || 0);
  if (kind === 'credit' && a > (own.kind === 'credit' ? ownAmt : 0) && a > limits.creditCap + NOTE_EPS) return { code: 'credit_cap', max: limits.creditCap };
  const after = r2(limits.outstandingBefore - (own.kind === 'credit' ? -ownAmt : ownAmt) + (kind === 'credit' ? -a : a));
  if (after < -NOTE_EPS && after < limits.outstandingBefore) return { code: 'outstanding_negative', after };
  return null;
}
/** A booked note's edit, in words: reason only — and, where Unbook is refused too, that refusal (the bill window's words);
 *  where it is not refused but this login has no Tax module (`tax` false), that Unbook is not this login's to do (the
 *  server's own `why` says the same — the b12 tail ship gate, the screens review's low). */
const bookedEditWhy = (n: AwPurchaseNote, t: TF, tax: boolean): string =>
  [t('aw_note_booked_edit'), n.book?.unbookRefusal ? noteWhy(n.book.unbookRefusal, t) : (!tax ? t('aw_pn_why_unbook_tax_module') : '')].filter(Boolean).join(' ');
/** An edit refused at click time (a race the pre-check could not see), in the reader's words, by the door's code. */
const noteEditErr = (e: any, t: TF): string => {
  const pl = e?.payload || {};
  const code = String(pl.code || '');
  if (!NOTE_EDIT_CODES.includes(code)) return pl.code ? noteErr(e, t) : perr(e, t);
  return t(`aw_note_err_${code}`, { max: amt(Number(pl.cap) || 0), after: amt(Number(pl.after) || 0) });
};
export function NoteDialog({ ctx, payable, note: target, onClose, onDone }: { ctx: AwCtx; payable?: AwPayable; note?: AwPurchaseNote; onClose: () => void; onDone: () => void }) {
  // `note` (the prop) is `target` in here: `note` is ui.ts's hint style, which this form uses too.
  const { t } = useTranslation();
  const edit = !!target;
  const [options, setOptions] = useState<AwPayable[] | null>(payable || edit ? (payable ? [payable] : []) : null);
  const [payableId, setPayableId] = useState(payable?.id || target?.payable_id || '');
  const [kind, setKind] = useState<AwPurchaseNoteKind>(target?.kind || 'credit');
  const [noteNo, setNoteNo] = useState(target?.note_no || '');
  const [noteDate, setNoteDate] = useState(target?.note_date || todayIso());
  const [amount, setAmount] = useState(target ? String(target.amount) : '');
  const [vat, setVat] = useState(target ? String(target.vat_amount) : '');
  const [reason, setReason] = useState(target?.reason || '');
  // Edit: the window's pre-check — the note as it stands now, its bill, the verdict and the limits.
  const [detail, setDetail] = useState<AwNoteDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — see BillDialog. The payables list, the note an edit opens with and its pre-check arriving from the server are
  // not edits: the flag is set by typing alone.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    if (payable || edit) return;
    let alive = true;
    loadAllPayables(ctx.clientId).then(rows => { if (alive) setOptions(rows.filter(isLive)); }).catch((e: any) => { if (alive) { setOptions([]); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!edit || !target) return;
    let alive = true;
    api.awPurchaseNote(ctx.clientId, target.id).then((d: AwNoteDetail) => { if (alive) setDetail(d); }).catch((e: any) => { if (alive) setErr(noteEditErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, edit, target?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The note as the server holds it now is what the form shows, as long as the operator has typed nothing.
  const fresh = detail?.note || null;
  useEffect(() => {
    if (!fresh || dirty) return;
    setKind(fresh.kind); setNoteNo(fresh.note_no); setNoteDate(fresh.note_date); setAmount(String(fresh.amount)); setVat(String(fresh.vat_amount)); setReason(fresh.reason);
  }, [fresh?.id, fresh?.note_no, fresh?.note_date, fresh?.amount, fresh?.vat_amount, fresh?.reason, fresh?.kind]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Until the pre-check lands, the row's own verdict (noteOut's `edit`, the same rule without its words) draws the form.
  const cur: AwPurchaseNote | null = fresh || target || null;
  const scope = detail?.edit || (target?.edit ? { ...target.edit, why: '' } : null);
  const p = edit ? null : (options || []).find(x => x.id === payableId) || null;
  const n = Number(amount), nVat = fin(vat);
  const figures = !edit || !!scope?.figures;
  // New: the server's credit cap (credit_total + amount ≤ to_pay, no 2-dp sum above it) checked here too, so the refusal is read before the request.
  const creditOver = !edit && !!p && kind === 'credit' && Number.isFinite(n) && r2(Number(p.credit_total) + n) > r2(Number(p.to_pay));
  // Edit: the door's cap (the note's own amount freed) and its floor, from the pre-check's limits.
  const breach = edit && cur && detail && figures && Number.isFinite(n) && n > 0 ? noteEditBreach(cur, detail.limits, kind, n) : null;
  // The door's own reading (create's): a reason of 1–500 characters (the New form keeps its 200, as it always had).
  const fieldsOk = Number.isFinite(n) && n > 0 && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0)) && isoDate(noteDate) && !!reason.trim() && okStr(noteNo)
    && (edit ? reason.trim().length <= 500 : okStr(reason));
  // What a Save would change — the PATCH carries these fields and nothing else.
  const changes: AwPurchaseNoteEditBody = {};
  if (edit && cur) {
    if (figures) {
      if (kind !== cur.kind) changes.kind = kind;
      if (noteNo.trim() !== cur.note_no) changes.note_no = noteNo.trim();
      if (noteDate !== cur.note_date) changes.note_date = noteDate;
      if (Number.isFinite(n) && r2(n) !== r2(cur.amount)) changes.amount = r2(n);
      if (r2(nVat || 0) !== r2(cur.vat_amount)) changes.vat_amount = r2(nVat || 0);
    }
    if (reason.trim() !== cur.reason) changes.reason = reason.trim();
  }
  const ready = edit
    ? !!detail && !!detail.edit && (detail.edit.figures || detail.edit.reason) && dirty && Object.keys(changes).length > 0 && fieldsOk && !breach
    : !!p && fieldsOk && !creditOver;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      if (edit && target) await api.awPurchaseNoteEdit(ctx.clientId, target.payable_id, target.id, changes);
      else if (p) await api.awPayableNoteAdd(ctx.clientId, p.id, { kind, note_no: noteNo.trim() || undefined, note_date: noteDate, amount: r2(n), vat_amount: r2(nVat || 0), reason: reason.trim() });
      onDone();
    } catch (e: any) { setErr(edit ? noteEditErr(e, t) : perr(e, t)); setBusy(false); }
  };
  // ⛔ No docNo while the number is an editable field (TL-13, the b9u rule); a booked note's number is a fact, so it heads the window.
  const lockedNo = edit && !!detail && !figures ? (cur?.note_no || target?.note_no || '') : '';
  return (
    <Dialog fullscreen title={edit ? t('aw_note_edit_title') : t('aw_note_new_title')} docNo={lockedNo || undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={edit ? 'ti ti-device-floppy' : 'ti ti-file-plus'} label={edit ? t('aw_doc_save') : t('aw_note_create_btn')} t={t} />}>
      <div data-note-form={edit ? 'edit' : 'new'} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={lbl}>{t('aw_note_payable')}</label>
          {edit ? (
            !fresh ? (err ? null : <Loader />) : (
              <div data-note-bill-fixed style={{ ...card, padding: '8px 12px', fontSize: 12.5 }}>
                <b>{fresh.bill_no}</b> · {fresh.vendor_name} · {t('aw_col_outstanding')} {amt(fresh.bill_outstanding)} {fresh.currency}
                <div style={{ ...note, marginTop: 4 }}>{t('aw_note_bill_fixed')}</div>
              </div>
            )
          ) : options === null ? <Loader /> : payable ? (
            <div style={{ ...card, padding: '8px 12px', fontSize: 12.5 }}><b>{payable.bill_no}</b> · {payable.vendor_name} · {t('aw_col_outstanding')} {amt(payable.outstanding)} {payable.currency}</div>
          ) : (
            <select value={payableId} onChange={e => ed(setPayableId, e.target.value)} style={field}>
              <option value="">{t('aw_note_pick_payable')}</option>
              {options.map(x => <option key={x.id} value={x.id}>{payableLabel(x)}</option>)}
            </select>
          )}
        </div>
        {edit && cur && scope && !scope.figures && (
          <>
            <div data-note-why={scope.code || ''} style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }}>
              <i className="ti ti-lock" aria-hidden /> {scope.code === 'bill_void' ? t('aw_note_err_bill_void') : bookedEditWhy(cur, t, hasTax(ctx))}
            </div>
            <div data-note-facts style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, fontSize: 12.5 }}>
              <div><label style={lbl}>{t('aw_col_type')}</label>{t(`aw_note_kind_${cur.kind}`)}</div>
              <div><label style={lbl}>{t('aw_note_no')}</label>{cur.note_no || '—'}</div>
              <div><label style={lbl}>{t('aw_col_date')}</label>{dmy(cur.note_date)}</div>
              <div><label style={lbl}>{t('aw_bank_amount')}</label><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(cur.amount)}</span></div>
              <div><label style={lbl}>{t('aw_pay_vat')}</label><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(cur.vat_amount)}</span></div>
            </div>
          </>
        )}
        {figures && (!edit || !!cur) && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <Field label={t('aw_col_type')}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" data-note-kind="credit" onClick={() => ed(setKind, 'credit')} style={seg(kind === 'credit')}>{t('aw_note_kind_credit')}</button>
                  <button type="button" data-note-kind="debit" onClick={() => ed(setKind, 'debit')} style={seg(kind === 'debit')}>{t('aw_note_kind_debit')}</button>
                </div>
              </Field>
              <Field label={t('aw_note_no')}><input data-note-field="note_no" value={noteNo} onChange={e => ed(setNoteNo, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
              <Field label={t('aw_col_date')}><input data-note-field="note_date" type="date" value={noteDate} onChange={e => ed(setNoteDate, e.target.value)} style={field} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label={t('aw_bank_amount')}>
                <input data-note-field="amount" type="number" step="0.01" min="0" value={amount} onChange={e => ed(setAmount, e.target.value)} style={field} />
                {creditOver && p && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_note_credit_cap', { max: amt(p.to_pay), used: amt(p.credit_total) })}</div>}
                {edit && detail && kind === 'credit' && (
                  <div data-note-cap={breach?.code === 'credit_cap' ? 'over' : 'ok'} style={{ fontSize: 11.5, color: breach?.code === 'credit_cap' ? '#B42318' : 'var(--text-tertiary)', marginTop: 3 }}>
                    {t('aw_note_cap_hint', { max: amt(detail.limits.creditCap) })}
                  </div>
                )}
                {breach?.code === 'outstanding_negative' && (
                  <div data-note-floor style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_note_floor_hint', { after: amt(breach.after) })}</div>
                )}
              </Field>
              <Field label={t('aw_pay_vat')}><input data-note-field="vat_amount" type="number" step="0.01" min="0" value={vat} onChange={e => ed(setVat, e.target.value)} style={field} /></Field>
            </div>
          </>
        )}
        {(!edit || (!!scope && scope.reason)) && (
          <Field label={t('aw_note_reason')}><textarea data-note-field="reason" value={reason} onChange={e => ed(setReason, e.target.value)} rows={2} maxLength={edit ? 500 : MAX_STR} style={{ ...field, resize: 'vertical' }} /></Field>
        )}
        <div style={note}>{t('aw_note_rule')}</div>
        {edit && !!target && <RecordEvidence clientId={ctx.clientId} kind="purchase_note" id={target.id} />}
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── A supplier note into the VAT return (batch 11 R8b, COA-D8; design §3.7) ─────────────────────────
// Booked or not is the server's answer on each note (`book`: services/purchaseNotes.ts noteBookOf). Whether Book note may
// be offered is its `bookable` pre-check (GET, always 200), asked only for the notes on screen that are not booked and
// only for a seat that may book — so the button is drawn where the POST would succeed and every other note says why, in
// words. Unbook and a booked note's Delete follow `book.unbookRefusal` / `deleteRefusal`, the doors' own withdrawRefusal.
// All three write the client's VAT return, so they need the Tax module (ctx.me.modules) as Book on the bill does; a
// read-only seat is drawn the chip and nothing else.
const NOTE_BOOK_CODES = ['tax_module', 'already_booked', 'not_thai', 'not_vat_client', 'currency', 'bill_not_booked', 'vat_month_filed',
  'line_exists', 'amount_invalid', 'filing_unreadable'];
const hasTax = (ctx: AwCtx): boolean => (ctx.me?.modules || []).includes('tax');
/** A note refusal in the reader's words, by its code; the server's own sentence for a code this file has no words for. */
function noteWhy(r: { code: string | null; why: string; entry?: string; asset?: string; line?: { docNo: string } | null; closed?: { start: string; end: string } }, t: TF): string {
  const code = String(r.code || '');
  // (Batch 11 W4 P9, LB-41) Posted in a closed period: it stays in the books — never "reverse it in Journal".
  if (code === 'posted' && r.closed?.start) return t('aw_er_why_posted_closed', { no: r.entry || '—', from: dmy(r.closed.start), to: dmy(r.closed.end) });
  if (code === 'posted') return t('aw_er_why_posted', { no: r.entry || '—' });
  if (code === 'asset') return t('aw_er_why_asset', { asset: r.asset || '—' });
  if (code === 'filed') return t('aw_er_why_filed_line');
  if (code === 'line_exists') return t('aw_pn_why_line_exists', { no: r.line?.docNo || '—' });
  if (NOTE_BOOK_CODES.includes(code)) return t(`aw_pn_why_${code}`);
  if (code === 'account_invalid' || code === 'not_booked') return t(`aw_pn_err_${code}`);
  return r.why;
}
/** A note write refused at click time (a race the pre-check could not see) — in the same words. */
const noteErr = (e: any, t: TF): string => {
  const pl = e?.payload || {};
  return pl.code ? noteWhy({ code: String(pl.code), why: String(e?.message || ''), entry: pl.entry, asset: pl.asset, line: pl.line, closed: pl.closed }, t) : perr(e, t);
};
/** The bookable answer of each note on screen that is not booked, keyed by note id — asked only when `on` (a seat that
 *  writes and has the Tax module), six at a time, again whenever the workspace reloads. */
function useNoteBookables(ctx: AwCtx, notes: AwPurchaseNote[], on: boolean): Record<string, AwNoteBookable> {
  const [map, setMap] = useState<Record<string, AwNoteBookable>>({});
  const key = on ? notes.filter(n => n.book?.state === 'not_booked').map(n => `${n.payable_id}/${n.id}`).join(',') : '';
  useEffect(() => {
    let alive = true;
    const ask = key ? key.split(',').map(k => k.split('/')) : [];
    (async () => {
      const out: Record<string, AwNoteBookable> = {};
      for (let i = 0; i < ask.length; i += 6) {
        const got = await Promise.all(ask.slice(i, i + 6).map(([pid, nid]) => api.awNoteBookable(ctx.clientId, pid, nid)
          .then((b: AwNoteBookable) => [nid, b] as const).catch(() => null)));
        for (const g of got) if (g) out[g[0]] = g[1];
      }
      if (alive) setMap(out);
    })();
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return map;
}
/** Where the note stands in the VAT return: Booked · its entry, or Not booked. */
function NoteBookChip({ n }: { n: AwPurchaseNote }) {
  const { t } = useTranslation();
  const b = n.book;
  if (!b) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  if (b.state !== 'booked') return <span data-pn-chip="not_booked" style={pillGrey}>{t('aw_pn_chip_not_booked')}</span>;
  const e = b.entry;
  return (
    <span data-pn-chip="booked" style={pillGreen}>
      {t('aw_pn_chip_booked')} · {e ? t(e.status === 'posted' ? 'aw_pn_entry_posted' : 'aw_pn_entry_draft', { no: e.docNo }) : t('aw_pn_entry_none')}
    </span>
  );
}
/** Edit / Book note / Unbook / Delete of one note, each drawn only where the server would accept it now (see above). Edit
 *  (batch 12 tail T1, FS-6) where the row's own verdict (noteOut's `edit`) lets anything change — never on a void bill's
 *  note — and only when the host passes `onEdit`. */
function NoteActions({ ctx, n, bookable, busy, onBook, onUnbook, onDelete, onEdit }: {
  ctx: AwCtx; n: AwPurchaseNote; bookable: AwNoteBookable | undefined; busy: string;
  onBook: (n: AwPurchaseNote, b: AwNoteBookable) => void; onUnbook: (n: AwPurchaseNote) => void; onDelete: (n: AwPurchaseNote) => void;
  onEdit?: (n: AwPurchaseNote) => void;
}) {
  const { t } = useTranslation();
  const b = n.book;
  const tax = hasTax(ctx);
  const booked = b?.state === 'booked';
  let book: React.ReactNode = null;
  if (b && !booked) {
    if (!tax) book = <span data-pn-why style={note}>{t('aw_pn_why_tax_module')}</span>;
    else if (bookable?.ok) {
      book = <button data-pn-book onClick={() => onBook(n, bookable)} disabled={!!busy} style={actBtn}><i className="ti ti-book" aria-hidden /> {t('aw_pn_book_btn')}</button>;
    } else if (bookable) book = <span data-pn-why style={note}>{noteWhy(bookable, t)}</span>;
  } else if (b && booked && tax) {
    book = b.unbookRefusal
      ? <span data-pn-why style={note}>{noteWhy(b.unbookRefusal, t)}</span>
      : <button data-pn-unbook onClick={() => onUnbook(n)} disabled={!!busy} style={actBtn}><i className={busy === `unbook-${n.id}` ? 'ti ti-loader' : 'ti ti-book-off'} aria-hidden /> {t('aw_pn_unbook_btn')}</button>;
  } else if (b && booked) {
    // (b12 tail ship gate) Without the Tax module a booked note is offered neither Unbook nor Delete (below): one sentence
    // says why — the refusal that stands against Unbook for everyone, else the module this login lacks.
    book = <span data-pn-why style={note}>{b.unbookRefusal ? noteWhy(b.unbookRefusal, t) : t('aw_pn_why_unbook_tax_module')}</span>;
  }
  // Delete as before for a note not booked; a booked one's Delete takes its line out first, so it needs the Tax module
  // and no refusal standing against that line.
  const canDelete = !booked || (tax && !b?.deleteRefusal);
  const canEdit = !!onEdit && !!n.edit && (n.edit.figures || n.edit.reason);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {canEdit && onEdit && (
        <button data-pn-edit onClick={() => onEdit(n)} disabled={!!busy} style={actBtn}><i className="ti ti-pencil" aria-hidden /> {t('aw_pn_edit')}</button>
      )}
      {book}
      {canDelete && (
        <button onClick={() => onDelete(n)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}>
          <i className={busy === n.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}
        </button>
      )}
    </div>
  );
}
/** Delete's question: a booked note's says its line comes out of the VAT return first. */
const noteDeleteConfirm = (n: AwPurchaseNote, t: TF): string => t(n.book?.state === 'booked' ? 'aw_pn_delete_booked_confirm' : 'aw_note_delete_confirm');

/** Book one note into the VAT return: what will be written (the signed line, its month) and the one choice — the
 *  expense account, pre-selected from the bill's own line. A card: one question. */
export function BookNoteDialog({ ctx, n, bill, bookable, onClose, onDone }: {
  ctx: AwCtx; n: AwPurchaseNote; bill: { bill_no: string; vendor_name: string }; bookable: AwNoteBookable; onClose: () => void; onDone: () => void;
}) {
  const { t } = useTranslation();
  const [account, setAccount] = useState(bookable.defaultAccountCode || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const pv = bookable.preview;
  const ready = bookable.ok && !!pv;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      // (Owner 2026-11-02) This window may have stood open for minutes, and a refused call pages the admins: asked first, a sentence if it moved.
      const v: AwNoteDoors = await api.awRowDoors(ctx.clientId, 'purchase_note', n.id);
      const shut = !v.found ? t('aw_doc_menu_gone') : v.bookable && !v.bookable.ok ? noteWhy(v.bookable, t) : '';
      if (shut) { setErr(shut); setBusy(false); return; }
      await api.awNoteBook(ctx.clientId, n.payable_id, n.id, account); onDone();
    } catch (e: any) { setErr(noteErr(e, t)); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_pn_dlg_title')} docNo={n.note_no} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-book" label={t('aw_pn_book_submit')} t={t} />}>
      <div data-pn-dialog style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {t('aw_pn_dlg_line', { kind: t(`aw_note_kind_${n.kind}`), no: n.note_no, date: dmy(n.note_date), bill: bill.bill_no, vendor: bill.vendor_name })}
        </div>
        {pv && (
          <div style={{ ...card, padding: '8px 12px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, fontSize: 12.5 }}>
            <div><label style={lbl}>{t('aw_pn_dlg_month')}</label>{`${String(pv.month).padStart(2, '0')}/${pv.year}`}</div>
            <div><label style={lbl}>{t('aw_pn_dlg_base')}</label><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(pv.amount)}</span></div>
            <div><label style={lbl}>{t('aw_pay_vat')}</label><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(pv.vat)}</span></div>
            <div><label style={lbl}>{t('aw_pay_total')}</label><b style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(pv.total)}</b></div>
          </div>
        )}
        <div>
          <label style={lbl}>{t('aw_pn_account')}</label>
          <select value={account} onChange={e => { setDirty(true); setAccount(e.target.value); }} style={field}>
            <option value="">{t('aw_pn_account_none')}</option>
            {bookable.accounts.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
          </select>
          {!!bookable.defaultAccountCode && <div style={note}>{t('aw_pn_account_hint')}</div>}
        </div>
        <div style={note}>{t('aw_pn_dlg_rule')}</div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── One supplier note, full screen (FS-6, batch 12 tail T1) ────────────────────────────────────────────────────────
// Opened from the notes list's number, the bill window's Notes table and — read-only, through sourceDoc.tsx NoteDocDialog —
// a voucher's or an entries panel's "via CN-…". Read from GET /purchase-notes/:nid (services/purchaseNotes.ts noteDetail):
// the note, its bill, where it stands in the VAT return, the last edit. Every write is one of the doors the window offers a
// writing seat — Edit (NoteDialog with the note) and the note's own Book note… / Unbook / Delete (NoteActions, the same
// pre-checks as the lists) — each opening over this window; a read-only seat gets Close and Entries only.
const BILL_STATUS_KEY: Record<string, string> = {
  open: 'aw_pay_status_open', partial: 'aw_pay_status_partial', paid: 'aw_pay_status_paid', void: 'aw_pay_status_void',
};
const NFact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ minWidth: 140, flex: '0 1 auto' }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 2 }}>{children}</div>
  </div>
);
/** The note's facts, read-only — this window's body and the voucher's read-only window's (sourceDoc.tsx NoteDocDialog).
 *  Every figure is the server's (the note's own; the bill's outstanding is the register's), never recomputed here; the
 *  base is the note's amount less its VAT, the one sum the note itself states. */
export function NoteFacts({ d }: { d: AwNoteDetail }) {
  const { t } = useTranslation();
  const n = d.note;
  const booked = n.book?.state === 'booked';
  return (
    <div data-note-view={n.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18 }}>
        <NFact label={t('aw_col_type')}><span style={n.kind === 'credit' ? pillGreen : pillAmber}>{t(`aw_note_kind_${n.kind}`)}</span></NFact>
        <NFact label={t('aw_note_no')}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{n.note_no || '—'}</b></NFact>
        <NFact label={t('aw_col_date')}>{n.note_date ? dmy(n.note_date) : '—'}</NFact>
        <NFact label={t('aw_bank_amount')}><b style={{ fontVariantNumeric: 'tabular-nums' }} data-note-amount>{amt(n.amount)}</b> <span style={{ color: 'var(--text-tertiary)' }}>{n.currency}</span></NFact>
        <NFact label={t('aw_pay_vat')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(n.vat_amount)}</span></NFact>
        <NFact label={t('aw_pn_dlg_base')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(r2(Number(n.amount) - Number(n.vat_amount)))}</span></NFact>
      </div>
      <div style={{ ...card, fontSize: 12.5 }}>
        <label style={lbl}>{t('aw_note_reason')}</label>
        <div style={{ whiteSpace: 'pre-wrap' }} data-note-reason>{n.reason || '—'}</div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_pay_bill')}</div>
        <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18 }}>
          <NFact label={t('aw_pay_bill_no')}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{n.bill_no || '—'}</b></NFact>
          <NFact label={t('aw_pay_vendor')}>{n.vendor_name || '—'}{n.vendor_tax_id ? <span style={{ color: 'var(--text-tertiary)' }}> · {n.vendor_tax_id}</span> : ''}</NFact>
          <NFact label={t('aw_col_currency')}>{n.currency || '—'}</NFact>
          <NFact label={t('aw_col_status')}>{BILL_STATUS_KEY[n.bill_status] ? t(BILL_STATUS_KEY[n.bill_status]) : (n.bill_status || '—')}</NFact>
          <NFact label={t('aw_col_outstanding')}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(n.bill_outstanding)}</b></NFact>
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_pn_col')}</div>
        <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'baseline' }}>
          <NFact label={t('aw_pn_col')}><NoteBookChip n={n} /></NFact>
          {booked && <NFact label={t('aw_pn_dlg_month')}>{n.note_date ? `${n.note_date.slice(5, 7)}/${n.note_date.slice(0, 4)}` : '—'}</NFact>}
          {booked && !!n.booked_by_name && <NFact label={t('aw_pay_booked')}>{n.booked_by_name} · {when(Number(n.booked_at) || 0)}</NFact>}
        </div>
      </div>
      <div style={note}>
        {t('aw_pay_by')}: {n.created_by_name || '—'} · {when(n.created_at)}
        {d.lastEdit && <div data-note-last-edit title={d.lastEdit.text}>{t('aw_note_last_edit', { by: d.lastEdit.byName || '—', when: when(d.lastEdit.at) })}</div>}
      </div>
    </div>
  );
}
export function NoteViewDialog({ ctx, id, onClose }: { ctx: AwCtx; id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [d, setD] = useState<AwNoteDetail | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [booking, setBooking] = useState<AwNoteBookable | null>(null);
  // A read-only seat (a client's own books login, the auditor) reads the note and is drawn no write (Documents.tsx's rule).
  const ro = isReadOnlySeat(ctx);
  const goToRecord = useGoToExpenseRecord();
  useEffect(() => {
    let alive = true;
    api.awPurchaseNote(ctx.clientId, id).then((x: AwNoteDetail) => { if (alive) { setD(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(noteEditErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const n = d?.note || null;
  const bookables = useNoteBookables(ctx, n ? [n] : [], !ro && hasTax(ctx));
  const run = async (key: string, fn: () => Promise<unknown>, after?: () => void) => {
    if (busy) return;
    setBusy(key); setErr('');
    try { await fn(); ctx.bump(); if (after) after(); } catch (e: any) { setErr(noteErr(e, t)); } finally { setBusy(''); }
  };
  const unbook = (x: AwPurchaseNote) => { if (window.confirm(t('aw_pn_unbook_confirm'))) run(`unbook-${x.id}`, () => api.awNoteUnbook(ctx.clientId, x.payable_id, x.id)); };
  // A deleted note has no window left to show: the delete closes it.
  const del = (x: AwPurchaseNote) => { if (window.confirm(noteDeleteConfirm(x, t))) run(x.id, () => api.awPayableNoteDelete(ctx.clientId, x.payable_id, x.id), onClose); };
  const canEdit = !!d && (d.edit.figures || d.edit.reason);
  return (
    <Dialog fullscreen title={n ? t(`aw_note_view_title_${n.kind}`) : t('aw_note_title')} docNo={n?.note_no || undefined} onClose={onClose}
      footer={(
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {n && <DocEntriesBtn ctx={ctx} kind="purchase_note" id={n.id} docNo={n.note_no} />}
          {/* "Open the bill in Expenses": a LINK to the register's own window, where the bill is worked on (the voucher's
              PayableDocDialog has the same) — it leaves through the unsaved guard, never past it. */}
          {!ro && !!d?.record?.key && (
            <button type="button" data-note-open-bill onClick={() => goToRecord(d.record!.key, onClose)} style={actBtn}>
              <i className="ti ti-external-link" aria-hidden /> {t('aw_note_open_bill')}
            </button>
          )}
          <div style={{ flex: 1 }} />
          {!ro && n && canEdit && (
            <button data-pn-edit onClick={() => setEditing(true)} disabled={!!busy} style={btnPrimary}><i className="ti ti-pencil" aria-hidden /> {t('aw_pn_edit')}</button>
          )}
          {!ro && n && <NoteActions ctx={ctx} n={n} bookable={bookables[n.id]} busy={busy} onBook={(_x, b) => setBooking(b)} onUnbook={unbook} onDelete={del} />}
          <GateCancel onClose={onClose} label={t('aw_bank_close')} />
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div style={errBox}>{err}</div>}
        {!d ? (!err && <Loader />) : <NoteFacts d={d} />}
        {/* Why the figures (or everything) cannot be edited now — a sentence, never a greyed button. */}
        {!!d && !ro && !d.edit.figures && (
          <div data-note-why={d.edit.code || ''} style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }}>
            <i className="ti ti-lock" aria-hidden /> {d.edit.code === 'bill_void' ? t('aw_note_err_bill_void') : bookedEditWhy(d.note, t, hasTax(ctx))}
          </div>
        )}
        {!!n && <RecordEvidence clientId={ctx.clientId} kind="purchase_note" id={n.id} />}
        {!ro && editing && n && <NoteDialog ctx={ctx} note={n} onClose={() => setEditing(false)} onDone={() => { setEditing(false); ctx.bump(); }} />}
        {!ro && booking && n && <BookNoteDialog ctx={ctx} n={n} bill={n} bookable={booking} onClose={() => setBooking(null)} onDone={() => { setBooking(null); ctx.bump(); }} />}
      </div>
    </Dialog>
  );
}

// ── Reason dialog (void a bill · cancel a PO · reverse a payment · void / return a scan) ─────────────
// (Batch 12 tail T2, TL-8) Full screen like every window of the four modules — a reason-only window included — its
// button pinned in the footer and the document it acts on in `docNo` (each of the nine call sites passes its number:
// the payment's, the bill's, the order's, the scan's file).
export function ReasonDialog({ title, docNo, line, hint, icon, label, onClose, onSubmit }: {
  title: string; docNo?: string; line: React.ReactNode; hint: string; icon: string; label: string; onClose: () => void; onSubmit: (reason: string) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — the reason a bill was voided is written once; the backdrop used to eat it in silence.
  const [dirty, setDirty] = useState(false);
  const ready = !!reason.trim() && okStr(reason);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await onSubmit(reason.trim()); } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={title} docNo={docNo || undefined} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={icon} label={label} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{line}</div>
        <div>
          <label style={lbl}>{t('aw_bank_ignore_reason')}</label>
          <textarea value={reason} onChange={e => { setDirty(true); setReason(e.target.value); }} rows={3} maxLength={MAX_STR} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{hint}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── The bill's sections, shared (batch 7 R3a, EXP-16) ───────────────────────────────────────────
// Lifted out of the payables drawer unchanged so the drawer (Expenses › Payments) and the expense
// record window (panes/ExpenseRecords.tsx) draw a bill from ONE set of components: the figures card,
// the action row, the payments, the notes and the statement lines that paid it. Each writes through
// the same /payables doors it always did, and every write re-reads through ctx.bump — the workspace's
// one signal — so the list behind the window moves too. Nothing here fetches the bill: the host does,
// once, and hands the detail down.
/** The busy / error / act triple a bill's writes run through — one per host window. */
export function usePayableActs(ctx: AwCtx) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const act = async (key: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(key); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  return { busy, err, setErr, act };
}
const FactRow = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ color: 'var(--text-secondary)' }}>{k}</span><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, textAlign: 'right' }}>{v}</span>
  </div>
);
/** The bill's figures, its identity line and its note. */
export function PayableFacts({ p }: { p: AwPayable }) {
  const { t } = useTranslation();
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
        <FactRow k={t('aw_pay_bill_date')} v={dmy(p.bill_date)} />
        {/* Days overdue only while the bill still owes — a paid or void bill past its due date is not late (the table's rule). */}
        <FactRow k={t('aw_pay_due_date')} v={p.due_date ? <>{dmy(p.due_date)}{isLive(p) && p.days_overdue > 0 && <span style={{ ...overduePill(p.days_overdue), marginLeft: 6 }}>{t('aw_ar_days', { n: p.days_overdue })}</span>}</> : '—'} />
        <FactRow k={t('aw_pay_total')} v={`${amt(p.total_amount)} ${p.currency}`} />
        <FactRow k={t('aw_pay_vat')} v={amt(p.vat_amount)} />
        <FactRow k={t('aw_pay_wht')} v={amt(p.wht_amount)} />
        <FactRow k={t('aw_pay_to_pay')} v={amt(p.to_pay)} />
        <FactRow k={t('aw_col_paid')} v={amt(p.paid_total)} />
        <FactRow k={t('aw_note_credit_total')} v={amt(p.credit_total)} />
        <FactRow k={t('aw_note_debit_total')} v={amt(p.debit_total)} />
        <FactRow k={t('aw_col_outstanding')} v={<b style={{ color: Number(p.outstanding) > 0 ? '#92400E' : 'inherit' }}>{amt(p.outstanding)}</b>} />
      </div>
      <div style={{ ...note, marginTop: 8 }}>
        {p.vendor_tax_id && <span>{t('aw_pay_vendor_tax_id')}: {p.vendor_tax_id} · </span>}
        {p.expense_filename && <span><i className="ti ti-paperclip" aria-hidden /> {t('aw_pay_linked_upload')}: {p.expense_filename} · </span>}
        {p.po_no && <span>{t('aw_po_no')}: {p.po_no} · </span>}
        {p.created_by_name && <span>{p.created_by_name} · {when(p.created_at)}</span>}
      </div>
      {p.note && <div style={{ fontSize: 12.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{p.note}</div>}
    </>
  );
}
/** The bill's action row. Void and Delete are offered only by a host that passes them (the record
 *  window); the drawer keeps the row it always had. `voidLocked` disables Void with the reason. */
/** (Batch 11 W4 P9, LB-49) Unbook's refusal (the bill read's `unbookRefusal`) in the reader's words, by its code; the server's
 *  sentence for a code with none. notes_booked / settlement_posted are said by their own locks (noteLock, settleLock). */
export function unbookWhy(r: { code: string; why: string; entry?: string; asset?: string; closed?: { start: string; end: string } }, t: TF): string {
  if (r.code === 'posted') return r.closed?.start ? t('aw_er_why_posted_closed', { no: r.entry || '—', from: dmy(r.closed.start), to: dmy(r.closed.end) }) : t('aw_er_why_posted', { no: r.entry || '—' });
  if (r.code === 'filed') return t('aw_er_why_filed_line');
  if (r.code === 'asset') return t('aw_er_why_asset', { asset: r.asset || '—' });
  if (r.code === 'notes_booked') return t('aw_er_why_notes_booked');
  return r.why;
}
export function PayableActions({ ctx, p, busy, act, onPay, onNote, onEdit, onBooked, onVoid, onDelete, voidLocked, notes, recordKey, bookRefusal, settleLock, bookBlock, unbookRefusal }: {
  ctx: AwCtx; p: AwPayable; busy: string; act: (key: string, fn: () => Promise<unknown>) => Promise<void>;
  onPay: (p: AwPayable) => void; onNote: (p: AwPayable) => void; onEdit: (p: AwPayable) => void; onBooked: () => void;
  onVoid?: (p: AwPayable) => void; onDelete?: (p: AwPayable) => void; voidLocked?: string;
  /** The bill's notes (the detail's `notes`, batch 11 R8b): while one is BOOKED into the VAT return it adjusts this bill's
   *  line, and the server refuses Unbook and Void (notes_booked, W2-36) — so the sentence stands where those buttons were. */
  notes?: AwPurchaseNote[];
  /** The expense record this bill is shown under (`u:…` / `p:…`) — what its entries are asked by (batch 11 R8a). */
  recordKey?: string;
  /** Batch 11 R3: the bill read's `bookRefusal` — Book would land on a VAT line still in the books with other figures (or
   *  one a booked purchase note holds); the sentence replaces the button, so a certain 409 is never sent. */
  bookRefusal?: AwPayableDetail['bookRefusal'];
  /** Batch 11 W3 R6 (W3-15): the bill read's `settleLock` — a payment of this bill is settled in the books, and the server
   *  refuses Unbook (and a VAT-moving edit) with settlement_posted until that settlement is reversed — so the sentence and
   *  "Reverse JV-…" (the entries panel, focused on it) stand where Unbook was. A booked note's lock is said first (the
   *  refusal order: notes_booked before settlement_posted). */
  settleLock?: AwPayableDetail['settleLock'];
  /** (W3 ship review) The bill read's `bookBlock`: one of Book's own refusals for this seat now (the Tax module, a company
   *  outside Thailand, a client not registered for VAT, a bill made from a scan already in the VAT return, a foreign
   *  currency, no real date, VAT above the total) — its sentence stands where Book was, so a certain refusal is never sent. */
  bookBlock?: AwPayableDetail['bookBlock'];
  /** (Batch 11 W4 P9, LB-49) The bill read's `unbookRefusal`: Unbook's own answer (its VAT line posted — 332 CJT bills —, in a
   *  filed month, an asset on it, a hand draft holding it…). Its sentence (and "Reverse JV-…" where the entry may be reversed)
   *  stands where Unbook was: an Unbook bound to be refused is never offered. */
  unbookRefusal?: AwPayableDetail['unbookRefusal'];
}) {
  const { t } = useTranslation();
  const noteLock = (notes || []).some(n => n.book?.state === 'booked');
  const settleLocked = !noteLock && !!p.tax_line_id && !!settleLock?.entries?.length;
  const unbookBlocked = !noteLock && !settleLocked && !!p.tax_line_id && !!unbookRefusal;
  // (W3 ship review, S4) Settlements posted in a period closed since are never reversed: they hold the bill for good.
  const settleShut = closedOf(settleLock?.entries);
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {isLive(p) && <button onClick={() => onPay(p)} style={{ ...btnPrimary, padding: '5px 11px', fontSize: 12 }}><i className="ti ti-cash" aria-hidden /> {t('aw_pay_record_btn_short')}</button>}
      {isLive(p) && <button onClick={() => onNote(p)} style={actBtn}><i className="ti ti-file-plus" aria-hidden /> {t('aw_note_add_btn')}</button>}
      {/* Every bill can be edited: a paid or void one opens note-only, as the server allows. */}
      <button onClick={() => onEdit(p)} style={actBtn}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>
      {/* PAY-18 / COA-D2 — what this bill posted, straight from the bill. ⛔ Offered on every
          bill, not only a booked one: whether anything was posted is the RESOLVER's answer
          (services/journalLinks.ts reads merchant_payables.tax_line_id itself), and a second
          copy of that test here would eventually hide the button on a bill that does have
          entries. The panel says plainly when nothing has been posted yet.
          ⛔ Batch 11 R8a: asked by the RECORD (kind 'expense'), once — its recognition, its notes' adjustments and
          every payment's settlement in one answer, where 'payable' read the bill's own line alone. */}
      <DocEntriesBtn ctx={ctx} kind="expense" id={recordKey || `p:${p.id}`} docNo={p.bill_no} />
      {/* Into the VAT return (phase 4b). The server refuses what it must — a voided bill, a
          foreign currency, a caller without the Tax module — and says which, so the button
          is offered on every bill that is not void rather than guessed at here. A booked one says so. */}
      {p.tax_line_id
        ? <>
            <span style={{ ...note, alignSelf: 'center' }}><i className="ti ti-book" aria-hidden /> {t('aw_pay_booked')}</span>
            {/* The undo. A filed month refuses it and says why — a filed return is
                corrected by an Additional Filing, never by deletion. */}
            {!noteLock && !settleLocked && !unbookBlocked && <button style={actBtn} disabled={!!busy}
              onClick={() => act('unbook', () => api.awPayableUnbook(ctx.clientId, p.id).then(onBooked))}>
              <i className={busy === 'unbook' ? 'ti ti-loader' : 'ti ti-book-off'} aria-hidden /> {t('aw_pay_unbook')}
            </button>}
            {unbookBlocked && unbookRefusal && (
              <span data-bill-unbook-refusal={unbookRefusal.code} style={{ ...note, alignSelf: 'center', maxWidth: 460, color: '#92400E', display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span><i className="ti ti-lock" aria-hidden /> {unbookWhy(unbookRefusal, t)}</span>
                {/* "Reverse JV-…" only where the entry may be reversed: posted, in an open period (one in a closed period stays). */}
                {unbookRefusal.code === 'posted' && !unbookRefusal.closed && !!unbookRefusal.entryId && (
                  <ReverseEntryBtn ctx={{ clientId: ctx.clientId }} kind="expense" id={recordKey || `p:${p.id}`} docNo={p.bill_no}
                    entry={{ entryId: unbookRefusal.entryId, docNo: unbookRefusal.entry, reversalDraft: unbookRefusal.reversalDraft || null }} />
                )}
              </span>
            )}
            {settleLocked && settleLock && (
              <span data-bill-settle-lock={settleLock.entries[0]?.docNo || ''} data-bill-settle-closed={settleShut.length ? settleShut.map(e => e.docNo || e.entryId.slice(0, 8)).join(',') : undefined}
                style={{ ...note, alignSelf: 'center', maxWidth: 460, color: '#92400E', display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span><i className="ti ti-lock" aria-hidden /> {settleShut.length
                  ? t('aw_bill_settle_lock_closed', { docNo: settleShut.map(e => e.docNo || e.entryId.slice(0, 8)).join(', '), from: dmy(settleShut[0].closed!.start), to: dmy(settleShut[0].closed!.end) })
                  : billLockText(t, settleLock.entries, false)}</span>
                {!settleShut.length && settleLock.entries.slice(0, 3).map(e => (
                  <ReverseEntryBtn key={e.entryId} ctx={{ clientId: ctx.clientId }} kind="bank_line" id={e.lineId} docNo={e.docNo} entry={{ entryId: e.entryId, docNo: e.docNo, reversalDraft: e.reversalDraft || null }} />
                ))}
              </span>
            )}
          </>
        // (Batch 11 W3 R6) Book on every bill that is not void — a PAID one too: a statement line that paid it in full is
        // reported `bill_not_booked` — "book it in Expense records" — so this window must offer the door that sentence names
        // (a live-only Book left a paid bill's line nowhere to go). (W3 ship review) But only where Book can succeed: one of
        // Book's own refusals for this seat (`bookBlock` — a scan already in the VAT return, a client not registered for VAT,
        // a foreign currency, no Tax module…) is said instead, so a request bound to be refused is never sent.
        : p.status !== 'void' && (bookBlock
          ? <span data-pay-book-block={bookBlock.code} style={{ ...note, alignSelf: 'center', maxWidth: 420 }}>
              <i className="ti ti-book-off" aria-hidden /> {(BOOK_BLOCK_CODES as readonly string[]).includes(bookBlock.code)
                ? t(`aw_pay_book_block_${bookBlock.code}`, { cur: bookBlock.params?.cur || '', home: bookBlock.params?.home || '' })
                : bookBlock.why}
            </span>
          : bookRefusal
          // The line Book would land on is a direct VAT line no document holds (a posted twin), so the way on is the
          // sentence naming its entry — Accounting › Journal entries (W2-46), not a document panel.
          ? <span data-ll-book={bookRefusal.code} style={{ ...note, alignSelf: 'center', maxWidth: 420, color: '#92400E' }}>
              <i className="ti ti-lock" aria-hidden /> {bookRefusal.code === 'note_held'
                ? t('aw_ll_book_note_held', { noteNo: bookRefusal.noteNo || '' })
                : (bookRefusal.entries || []).some(e => !!e.closed && !e.reversalDraft)
                  // (W2 ship, S4) its entry posted in a closed period is not reversed: never "reverse it first".
                  ? t('aw_ll_book_in_books_closed', { docNos: (bookRefusal.entries || []).map(e => e.docNo).join(', ') })
                  : t('aw_ll_book_in_books', { docNos: (bookRefusal.entries || []).map(e => e.docNo).join(', ') })}
            </span>
          : <button style={actBtn} disabled={!!busy}
            onClick={() => act('book', () => api.awPayableBook(ctx.clientId, p.id).then(onBooked))}>
            <i className={busy === 'book' ? 'ti ti-loader' : 'ti ti-book'} aria-hidden /> {t('aw_pay_book')}
          </button>)}
      {onVoid && isLive(p) && !noteLock && (
        <button onClick={() => onVoid(p)} disabled={!!busy || !!voidLocked} title={voidLocked || undefined} style={{ ...actBtn, opacity: voidLocked ? 0.5 : 1 }}>
          <i className="ti ti-ban" aria-hidden /> {t('aw_pay_void_btn')}
        </button>
      )}
      {/* Batch 11 R8b: a booked supplier note holds the bill's line — said once, instead of Unbook and Void. */}
      {noteLock && (p.tax_line_id || (onVoid && isLive(p))) && <span data-pn-bill-lock style={{ ...note, alignSelf: 'center' }}><i className="ti ti-lock" aria-hidden /> {t('aw_pn_bill_locked')}</span>}
      {/* Delete is only offered where the server would accept it: a bill with no payment and no note. */}
      {onDelete && !Number(p.paid_total) && !Number(p.credit_total) && !Number(p.debit_total) && (
        <button onClick={() => onDelete(p)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}>
          <i className={busy === `del-${p.id}` ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}
        </button>
      )}
    </div>
  );
}
/** What one payment did to the books (batch 11 R8a, design §3.6.1): a recorded payment (R5's master, PMT-…) by its own
 *  kind — its statement line's settlement and its bills' recognition — and a payment from before batch 11 by the
 *  statement line it was matched to. Nothing for an earlier payment with no line: it never reached the books. */
function PaymentEntriesBtn({ ctx, x }: { ctx: AwCtx; x: { bank_line_id?: string; paid_date: string; reference?: string; expensePaymentId?: string; payNo?: string } }) {
  const face = <i className="ti ti-book-2" aria-hidden />;
  const style: React.CSSProperties = { ...actBtn, padding: '1px 6px', marginLeft: 6 };
  if (x.expensePaymentId) return <DocEntriesBtn ctx={ctx} kind="expense_payment" id={x.expensePaymentId} docNo={x.payNo || x.reference || ''} label={face} style={style} />;
  if (x.bank_line_id) return <DocEntriesBtn ctx={ctx} kind="bank_line" id={x.bank_line_id} docNo={dmy(x.paid_date)} label={face} style={style} />;
  return null;
}
/** The bill's payment history (batch 11 R5, W2-22; design §3.2.6): every payment — newest first, 100 a page, a reversed
 *  one STRUCK with its reason, who and when. Each row: its PMT (or "before payment numbers"), date, method, what was
 *  paid, the WHT it took (and the 50 ทวิ, for a seat with the Tax module), where the money came from, its settlement in
 *  the books, its slips. Actions, never for a read-only seat (the books seat, the auditor): "Reverse PMT-…" on a batch
 *  11 payment and "Delete…" on an older one, each with its reason (ReasonDialog) and each asked first — the reversible
 *  pre-check, the row's own settlement — so neither is offered where the server would refuse: where the statement
 *  line's settlement is still in the books the row says so and opens that entry instead. ⛔ No window.confirm. */
export function PayablePayments({ ctx, p, d, busy, act, readOnly, onPage }: {
  ctx: AwCtx; p: AwPayable; d: AwPayableDetail; busy: string; act: (key: string, fn: () => Promise<unknown>) => Promise<void>; readOnly?: boolean;
  /** The history's page (100 a page); the host re-reads the bill with ?paymentsPage=. */
  onPage?: (n: number) => void;
}) {
  const { t } = useTranslation();
  const canTax = ctx.me.modules.includes('tax');
  const [rev, setRev] = useState<{ x: AwPayablePayment; r: EpayReversibility | null } | null>(null);
  const [why, setWhy] = useState<Record<string, { text: string; entry?: { id: string; docNo: string } }>>({});
  const [fileErr, setFileErr] = useState('');
  const settleText = (x: AwPayablePayment) => x.settle ? t(`aw_epay_settle_${x.settle.state}`, { docNo: x.settle.entry?.docNo || '' }) : '—';
  // ── The settlement cell (batch 11 W3 R6, P6c task 3; design §3.3.3) — by the row's own settle state, read by its holder:
  //   · ready → "Create draft entry" (a seat that writes the books): Generate for THIS line only (`only`, no period), then
  //     the row is read again — it shows the draft, and the row's entries button (R8a) is where Post is;
  //   · not_in_books with a skip → why, in the reader's language (settleSkipText, the report's words); the month's hand
  //     vouchers → "Review N vouchers" (SettleAckDialog — a read for a read-only seat); another bill not booked → its window;
  //   · reversed → which entry, and which reversal took it out (`previous`);
  //   · draft / posted / no_line → as before.
  // ⛔ No button but on `ready` writes anything, so no request the server will certainly refuse is ever sent.
  const canGenerate = !readOnly && (ctx.me?.modules || []).includes('accounting_books');
  const [acking, setAcking] = useState<AwPayablePayment | null>(null);
  const lineOf = (x: AwPayablePayment): string => String(x.line?.id || x.bank_line_id || '');
  // (Batch 11 W4 R7) A cash payment's settlement source is its petty cash row (cash_line), never a statement line.
  const rowOf = (x: AwPayablePayment): string => (x.source === 'cash' ? String(x.cashRowId || '') : '');
  const srcOf = (x: AwPayablePayment): { kind: 'bank_line' | 'cash_line'; id: string } => rowOf(x) ? { kind: 'cash_line', id: rowOf(x) } : { kind: 'bank_line', id: lineOf(x) };
  const settleCell = (x: AwPayablePayment, gone: boolean): React.ReactNode => {
    const st = x.settle;
    if (!st) return '—';
    if (st.state === 'reversed' && st.previous) {
      return <span data-epay-settle="reversed">{t('aw_epay_settle_reversed_by', { docNo: st.previous.docNo || '—', reversal: st.previous.reversalDocNo || '—' })}</span>;
    }
    const head = <span data-epay-settle={st.state}>{settleText(x)}</span>;
    if (gone) return head;
    if (st.state === 'ready' && canGenerate && srcOf(x).id) {
      return (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {head}
          <button type="button" disabled={!!busy} data-epay-create={x.id} style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}
            onClick={() => act(`gen-${x.id}`, () => settleApi.run(ctx.clientId, { dryRun: false, only: rowOf(x) ? { cashRowIds: [rowOf(x)] } : { bankLineIds: [lineOf(x)] } }))}>
            <i className={busy === `gen-${x.id}` ? 'ti ti-loader' : 'ti ti-file-plus'} aria-hidden /> {t('aw_epay_create_draft')}
          </button>
        </span>
      );
    }
    if (st.state === 'not_in_books' && st.skip) {
      const sk = st.skip;
      const n = Number(sk.candidates) || Number(sk.params?.n) || 0;
      // bill_not_booked over a payment of several bills: the first other bill not booked opens its own window (this bill's
      // Book is right here). params carry the ids and the numbers in the same order.
      const ids = sk.code === 'bill_not_booked' ? String(sk.params?.billIds || '').split(',').map(v => v.trim()) : [];
      const nos = String(sk.params?.bills || '').split(', ');
      const at = ids.findIndex(id => !!id && id !== p.id);
      const otherBill = at >= 0 ? ids[at] : '';
      return (
        <span style={{ display: 'inline-block', whiteSpace: 'normal', maxWidth: 320 }}>
          {head}
          <div style={{ fontSize: 11.5, color: '#92400E', marginTop: 2 }} title={sk.why || undefined} data-epay-skip={sk.code}>{settleSkipText(t, { code: sk.code, why: sk.why, params: sk.params })}</div>
          {sk.code === 'manual_settlement_candidates' && !!srcOf(x).id && (
            <button type="button" onClick={() => setAcking(x)} data-epay-review={x.id} style={{ ...actBtn, marginTop: 3 }}>
              <i className="ti ti-list-check" aria-hidden /> {t('aw_epay_review_vouchers', { n })}
            </button>
          )}
          {!!otherBill && <OpenBillBtn ctx={ctx} goTo={{ kind: 'payable', id: otherBill, docNo: nos[at] || '' }} onClosed={() => ctx.bump()} />}
        </span>
      );
    }
    return head;
  };
  // A settlement still in the books: the row's own state says so first (a batch 11 payment's master line, or an older
  // payment's own line — services/expensePayments.ts settleStateOfLine).
  const settled = (x: AwPayablePayment) => x.settle?.state === 'posted';
  const askReverse = (x: AwPayablePayment) => act(`rev-${x.id}`, async () => {
    const r = await epayApi.reversible(ctx.clientId, String(x.expensePaymentId));
    if (r.ok) { setRev({ x, r }); return; }
    setWhy(w => ({ ...w, [x.id]: r.code === 'settlement_posted' && r.entry
      ? { text: t(rowOf(x) ? 'aw_epay_settle_posted_why_cash' : 'aw_epay_settle_posted_why', { docNo: r.entry.docNo }), entry: { id: srcOf(x).id, docNo: r.entry.docNo } }
      : { text: t(`aw_epay_err_${r.code || 'generic'}`) } }));
  });
  const addFiles = (x: AwPayablePayment, list: FileList | null) => {
    const add = Array.from(list || []);
    const bad = add.map(f => !(EPAY_FILE_TYPES as readonly string[]).includes(f.type) ? t('aw_epay_file_bad_type', { name: f.name })
      : f.size > EPAY_FILE_MAX ? t('aw_epay_file_too_big', { name: f.name }) : '').filter(Boolean);
    const over = (x.files || []).length + add.length > EPAY_FILES_PER_PAYMENT;
    setFileErr(bad[0] || (over ? t('aw_epay_files_max') : ''));
    if (!add.length || bad.length || over) return;
    act(`files-${x.id}`, () => epayApi.addFiles(ctx.clientId, String(x.expensePaymentId), add));
  };
  const total = Number(d.paymentsTotal) || d.payments.length;
  const clipsNew = useAttachmentCounts(ctx.clientId, 'expense_payment', d.payments.filter(x => !!x.expensePaymentId).map(x => String(x.expensePaymentId)));
  const clipsOld = useAttachmentCounts(ctx.clientId, 'payable_payment', d.payments.filter(x => !x.expensePaymentId).map(x => x.id));
  // The bill is read again when the paperclip's window closes, as the ✕ it replaced did: a slip removed in there must leave its chip, whose click would otherwise answer a 404 that pages the admins.
  const clipClosed = () => { clipsNew.refresh(); clipsOld.refresh(); ctx.bump(); };
  return (
    <div>
      <div style={sectionHead}>{t('aw_pay_payments')}</div>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!d.payments.length ? <div style={emptyLine}>{t('aw_pay_no_payments')}</div> : (
          <table style={table} data-epay-history>
            <thead><tr>
              <th style={th}>{t('aw_epay_col_pmt')}</th><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_pay_method')}</th>
              <th style={{ ...th, textAlign: 'right' }}>{t('aw_epay_col_cash')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_epay_col_wht')}</th>
              <th style={th}>{t('aw_epay_col_from')}</th><th style={th}>{t('aw_epay_col_books')}</th><th style={th}>{t('aw_epay_col_files')}</th>
              <th style={th}>{t('aw_pay_by')}</th>{!readOnly && <th style={th}>{t('aw_col_actions')}</th>}
            </tr></thead>
            <tbody>
              {d.payments.map(x => {
                const gone = !!x.deleted;
                const strike: React.CSSProperties = gone ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : {};
                const v2 = !!x.expensePaymentId;
                const w = why[x.id];
                return (
                  <React.Fragment key={x.id}>
                    <tr data-epay-row={x.id} data-epay-gone={gone ? '1' : undefined}>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, ...strike }}>{v2 ? x.payNo : <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>{t('aw_epay_old')}</span>}
                        {x.multi && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary)' }}>{t('aw_epay_multi')}</div>}
                        {/* The records it pays (2026-11-02, Automatic Expense Number): a payment has no window of its own, this row is it.
                            ⛔ The cell is nowrap and one payment can pay up to 100 bills: unwrapped, the numbers push Actions out of view. */}
                        {!!x.expenseNos?.length && <div data-epay-exp-nos={x.id} title={t('aw_er_col_no')} style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'normal', minWidth: 110, maxWidth: 220 }}>{x.expenseNos.join(' · ')}</div>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', ...strike }}>{dmy(x.paid_date)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', ...strike }}>{t(`aw_pay_method_${x.method}`) || x.method}<PaymentEntriesBtn ctx={ctx} x={x} /></td>
                      <td style={{ ...num, ...strike }}>{amt(x.amount)}</td>
                      <td style={{ ...num, ...strike }}>{Number(x.whtAmount) ? amt(x.whtAmount) : '—'}
                        {!gone && !readOnly && canTax && !!x.whtLine && (
                          <div><button type="button" disabled={!!busy} onClick={() => act(`tawi-${x.id}`, () => api.tawi50Pdf(ctx.clientId, [String(x.whtLine?.id)], `50tawi-${x.payNo || p.bill_no}.pdf`))} style={{ ...actBtn, marginTop: 3 }} data-epay-tawi={x.id}>
                            <i className={busy === `tawi-${x.id}` ? 'ti ti-loader' : 'ti ti-file-certificate'} aria-hidden /> {t('aw_epay_tawi')}
                          </button></div>
                        )}
                      </td>
                      <td style={{ ...td, ...strike }} data-epay-from={x.box ? 'box' : undefined}>{x.box ? t('aw_epay_source_box', { name: x.box.name || '—' }) : x.bankAccount?.label || (x.bank_line_id ? <span style={{ ...pillBlue }}><i className="ti ti-link" aria-hidden /> {t('aw_bank_match')}</span> : '—')}
                        {x.line && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{dmy(x.line.date)} · {amt(x.line.amount)}</div>}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', ...strike }}>{settleCell(x, gone)}</td>
                      <td style={td}>
                        {(x.files || []).map(f => (
                          <div key={f.id} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <button type="button" onClick={() => act(`dl-${f.id}`, () => epayApi.downloadFile(ctx.clientId, String(x.expensePaymentId), f))} style={{ ...actBtn, padding: '2px 6px' }} title={f.filename}>
                              <i className="ti ti-paperclip" aria-hidden /> {f.filename.length > 18 ? `${f.filename.slice(0, 16)}…` : f.filename}
                            </button>
                          </div>
                        ))}
                        {/* (Owner 2026-11-02, "Document Attachments & Linked Records") The payment's card, in the paperclip's window: a payment has no window of its own (sourceDoc.tsx). The ✕ that
                            stood beside each file took it off with no question and no trail; Remove lives there now, with a confirmation, a reason and the locks. */}
                        <AttachmentsBtn clientId={ctx.clientId} kind={v2 ? 'expense_payment' : 'payable_payment'} id={v2 ? String(x.expensePaymentId) : x.id} docNo={x.payNo || p.bill_no}
                          count={v2 ? clipsNew.counts[String(x.expensePaymentId)] : clipsOld.counts[x.id]} onClosed={clipClosed} />
                        {v2 && !readOnly && !gone && (x.files || []).length < EPAY_FILES_PER_PAYMENT && (
                          <label style={{ ...actBtn, padding: '2px 6px', cursor: 'pointer', display: 'inline-flex' }} title={t('aw_epay_files_hint')}>
                            <i className="ti ti-upload" aria-hidden /> {t('aw_epay_files_add')}
                            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={e => { addFiles(x, e.target.files); e.target.value = ''; }} />
                          </label>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{x.created_by_name}<div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{when(x.created_at)}</div></td>
                      {!readOnly && <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {!gone && (settled(x) ? (
                          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', whiteSpace: 'normal', maxWidth: 260, fontSize: 11.5, color: '#92400E' }} data-epay-settled={x.id}>
                            {t(rowOf(x) ? 'aw_epay_settle_posted_why_cash' : 'aw_epay_settle_posted_why', { docNo: x.settle?.entry?.docNo || '' })}
                            {rowOf(x) ? <DocEntriesBtn ctx={ctx} kind="cash_line" id={rowOf(x)} docNo={x.settle?.entry?.docNo || ''} />
                              : !!x.line && <DocEntriesBtn ctx={ctx} kind="bank_line" id={x.line.id} docNo={x.settle?.entry?.docNo || ''} />}
                          </span>
                        ) : v2 ? (
                          <button type="button" onClick={() => askReverse(x)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} data-epay-reverse={x.id}>
                            <i className={busy === `rev-${x.id}` ? 'ti ti-loader' : 'ti ti-arrow-back-up'} aria-hidden /> {t('aw_epay_reverse_btn', { payNo: x.payNo })}
                          </button>
                        ) : (
                          <button type="button" onClick={() => setRev({ x, r: null })} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} title={x.bank_line_id ? t('aw_pay_unpay_unmarks') : undefined} data-epay-old-delete={x.id}>
                            <i className="ti ti-trash" aria-hidden /> {t('aw_bank_delete')}…
                          </button>
                        ))}
                        {w && <div style={{ fontSize: 11.5, color: '#92400E', whiteSpace: 'normal', maxWidth: 260 }} data-epay-why={x.id}>{w.text}{w.entry?.id && <> {rowOf(x)
                          ? <DocEntriesBtn ctx={ctx} kind="cash_line" id={w.entry.id} docNo={w.entry.docNo} />
                          : <DocEntriesBtn ctx={ctx} kind="bank_line" id={w.entry.id} docNo={w.entry.docNo} />}</>}</div>}
                      </td>}
                    </tr>
                    {gone && (
                      <tr><td colSpan={readOnly ? 9 : 10} style={{ ...td, paddingTop: 0, fontSize: 11.5, color: 'var(--text-tertiary)' }} data-epay-reversed={x.id}>
                        {x.reverseReason ? t('aw_epay_reversed_by', { by: x.deletedByName || '—', when: when(Number(x.deletedAt)), reason: x.reverseReason })
                          : t('aw_epay_taken_off_by', { by: x.deletedByName || '—', when: when(Number(x.deletedAt)) })}
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {fileErr && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 4 }}>{fileErr}</div>}
      {onPage && total > 100 && <Pagination page={Number(d.paymentsPage) || 1} total={total} limit={100} onPage={onPage} />}
      {rev && (
        <ReasonDialog
          title={rev.x.expensePaymentId ? t('aw_epay_reverse_title', { payNo: rev.x.payNo }) : t('aw_epay_old_delete_title')}
          docNo={rev.x.payNo || p.bill_no}
          line={<>
            <b style={{ color: 'var(--text-primary)' }}>{rev.x.payNo || p.bill_no}</b> · {dmy(rev.x.paid_date)} · {amt(rev.x.amount)} {p.currency}
            {(rev.r?.whtEffects || []).map(e => (
              <div key={e.whtLineId} style={{ marginTop: 4 }}>{t(`aw_epay_reverse_wht_${e.action}`)}{e.certNo ? ` ${t('aw_epay_reverse_cert', { cert: e.certNo })}` : ''}</div>
            ))}
            {!!rev.r?.draftsToDiscard?.length && <div style={{ marginTop: 4 }}>{t('aw_epay_reverse_drafts', { docNos: rev.r.draftsToDiscard.join(', ') })}</div>}
          </>}
          hint={rev.x.expensePaymentId ? t('aw_epay_reverse_hint') : t('aw_epay_old_delete_hint')}
          icon="ti ti-arrow-back-up" label={rev.x.expensePaymentId ? t('aw_epay_reverse_btn', { payNo: rev.x.payNo }) : t('aw_bank_delete')}
          onClose={() => setRev(null)}
          onSubmit={async reason => {
            if (rev.x.expensePaymentId) await epayApi.reverse(ctx.clientId, rev.x.expensePaymentId, reason);
            else await api.awPayableUnpay(ctx.clientId, p.id, rev.x.id, reason);
            setRev(null); ctx.bump();
          }} />
      )}
      {acking && (
        <SettleAckDialog ctx={ctx} source={srcOf(acking)} line={acking.line ? { date: acking.line.date, amount: acking.line.amount } : { date: acking.paid_date, amount: Number(acking.amount) || 0 }}
          onClose={() => setAcking(null)} onDone={remaining => { if (!remaining) setAcking(null); ctx.bump(); }} />
      )}
    </div>
  );
}
/** The debit / credit notes against the bill: each with where it stands in the VAT return (batch 11 R8b) and — not for a
 *  read-only login — Edit (batch 12 tail T1), Book note / Unbook and its delete. Its number opens the note's own full-screen
 *  window (NoteViewDialog — a read, for every seat), over the bill's window, which stays mounted behind it. */
export function PayableNotes({ ctx, p, d, busy, act, readOnly }: { ctx: AwCtx; p: AwPayable; d: AwPayableDetail; busy: string; act: (key: string, fn: () => Promise<unknown>) => Promise<void>; readOnly?: boolean }) {
  const { t } = useTranslation();
  const bookables = useNoteBookables(ctx, d.notes, !readOnly && hasTax(ctx));
  const [booking, setBooking] = useState<{ n: AwPurchaseNote; b: AwNoteBookable } | null>(null);
  const [viewing, setViewing] = useState('');
  const [editing, setEditing] = useState<AwPurchaseNote | null>(null);
  const unbook = (x: AwPurchaseNote) => { if (window.confirm(t('aw_pn_unbook_confirm'))) act(`unbook-${x.id}`, () => api.awNoteUnbook(ctx.clientId, p.id, x.id)); };
  const del = (x: AwPurchaseNote) => { if (window.confirm(noteDeleteConfirm(x, t))) act(x.id, () => api.awPayableNoteDelete(ctx.clientId, p.id, x.id)); };
  return (
    <div>
      <div style={sectionHead}>{t('aw_note_title')}</div>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!d.notes.length ? <div style={emptyLine}>{t('aw_note_none_for_bill')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_col_type')}</th><th style={th}>{t('aw_note_no')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_pay_vat')}</th><th style={th}>{t('aw_note_reason')}</th><th style={th}>{t('aw_pn_col')}</th>{!readOnly && <th style={th}>{t('aw_col_actions')}</th>}</tr></thead>
            <tbody>
              {d.notes.map(x => (
                <tr key={x.id} data-note-row={x.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(x.note_date)}</td>
                  <td style={td}><span style={x.kind === 'credit' ? pillGreen : pillAmber}>{t(`aw_note_kind_${x.kind}`)}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button type="button" data-pn-view={x.id} onClick={() => setViewing(x.id)} title={t(`aw_note_view_title_${x.kind}`)} style={noteLink}>{x.note_no || '—'}</button>
                    <DocEntriesBtn ctx={ctx} kind="purchase_note" id={x.id} docNo={x.note_no} label={<i className="ti ti-book-2" aria-hidden />} style={{ ...actBtn, padding: '1px 6px', marginLeft: 6 }} />
                  </td>
                  <td style={num}>{amt(x.amount)}</td>
                  <td style={num}>{amt(x.vat_amount)}</td>
                  <td style={td}>{x.reason}</td>
                  <td style={td}><NoteBookChip n={x} /></td>
                  {!readOnly && <td style={td}>
                    <NoteActions ctx={ctx} n={x} bookable={bookables[x.id]} busy={busy} onBook={(n, b) => setBooking({ n, b })} onUnbook={unbook} onDelete={del} onEdit={setEditing} />
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!readOnly && booking && <BookNoteDialog ctx={ctx} n={booking.n} bill={p} bookable={booking.b} onClose={() => setBooking(null)} onDone={() => { setBooking(null); ctx.bump(); }} />}
      {viewing && <NoteViewDialog ctx={ctx} id={viewing} onClose={() => setViewing('')} />}
      {!readOnly && editing && <NoteDialog ctx={ctx} note={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); ctx.bump(); }} />}
    </div>
  );
}
/** The statement lines that paid the bill. A line a batch 11 payment holds is freed only by reversing that payment
 *  (its row above: "Reverse PMT-…" — the server answers a bare unmatch 409, W2-18), so it carries its PMT and no button
 *  here; a line an older payment was recorded from comes off with a reason (D12); one marked 'payable' by hand is
 *  unmatched as before. Not for a read-only login. */
export function PayableBankLines({ ctx, d, busy, act, readOnly }: { ctx: AwCtx; d: AwPayableDetail; busy: string; act: (key: string, fn: () => Promise<unknown>) => Promise<void>; readOnly?: boolean }) {
  const { t } = useTranslation();
  const [undo, setUndo] = useState<AwBankLine | null>(null);
  // An older payment's line whose settlement is still in the books (its payment row says so) is not offered at all.
  const settledLine = (l: AwBankLine) => d.payments.some(x => !x.deleted && x.bank_line_id === l.id && x.settle?.state === 'posted');
  return (
    <div>
      <div style={sectionHead}>{t('aw_pay_bank_lines')}</div>
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {!d.bank_lines.length ? <div style={emptyLine}>{t('aw_pay_no_bank_lines')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_bank_account')}</th><th style={th}>{t('aw_bank_description')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_debit')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_match')}</th>{!readOnly && <th style={th}>{t('aw_col_actions')}</th>}</tr></thead>
            <tbody>
              {d.bank_lines.map(l => (
                <tr key={l.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(l.txn_date)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{l.account_number}</td>
                  <td style={td}>{l.description}{l.details && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{l.details}</div>}</td>
                  <td style={num}>{amt(l.debit)}</td>
                  <td style={num}>{amt(l.match_amount)}</td>
                  {!readOnly && <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {l.expense_payment ? <span style={pillBlue} data-epay-held={l.id}><i className="ti ti-link" aria-hidden /> {t('aw_epay_held_by', { payNo: l.expense_payment.payNo })}</span>
                      : settledLine(l) ? null
                      : l.match_kind === 'payment' ? (
                        <button onClick={() => setUndo(l)} disabled={!!busy} style={actBtn} title={t('aw_pay_unmatch_hint')}>
                          <i className="ti ti-link-off" aria-hidden /> {t('aw_epay_undo_old')}
                        </button>
                      ) : (
                        // Unmatching a hand-marked line reverses what the match recorded here, the spec's one door in both directions.
                        <button onClick={() => act(l.id, () => api.awBankUnmatch(ctx.clientId, l.id))} disabled={!!busy} style={actBtn} title={t('aw_pay_unmatch_hint')}>
                          <i className={busy === l.id ? 'ti ti-loader' : 'ti ti-link-off'} aria-hidden /> {t('aw_bank_unmatch_btn')}
                        </button>
                      )}
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {undo && (
        <ReasonDialog title={t('aw_epay_old_delete_title')} docNo={`${dmy(undo.txn_date)} · ${undo.description || ''}`.trim()} line={<><b style={{ color: 'var(--text-primary)' }}>{dmy(undo.txn_date)}</b> · {undo.description} · {amt(undo.debit)}</>}
          hint={t('aw_epay_old_delete_hint')} icon="ti ti-link-off" label={t('aw_epay_undo_old')} onClose={() => setUndo(null)}
          onSubmit={async reason => { await api.awBankUnmatch(ctx.clientId, undo.id, reason); setUndo(null); ctx.bump(); }} />
      )}
    </div>
  );
}

// ── Payable detail drawer — GONE with Purchases › Payments (batch 7 R5) ────────────────────────
// It was the only way into a bill from the old list. The register's full-screen record window
// shows the same bill with its scan beside it (panes/ExpenseRecords.tsx), built from the cards
// below (PayableFacts / PayablePayments / PayableNotes / PayableBankLines / PayableActions),
// which is why those stayed and this did not.

// ── Purchases › Payments — GONE (batch 7 R5) ───────────────────────────────────────────────────
// The payables register lived here and showed one half of an expense: the bill, with the scan it
// came from somewhere else entirely. Expenses › Expense records now holds the pair as one row with
// the payment on it, so this pane had nothing left of its own to show. Its dialogs and cards did
// NOT go with it — BillDialog, PaymentDialog, NoteDialog, ReasonDialog, PayableActions and the
// rest are exported from this file and the register renders them. The old addresses
// expenses/payments and purchases/payments are aliases now (menus.ts MOVED), carrying pay=unpaid
// so a bookmark lands on the same list it used to.

// ── Purchases › Debit & credit notes — every note across the payables ───────────────────────────
// Read from GET /purchase-notes (the server half's additive list: server paging, kind and search
// filters). The contract itself (SPEC-B3 §2) has no notes list, so when that route answers 404 the
// pane gathers the notes from the bills that carry any — the register's credit_total / debit_total
// caches say which — a few details at a time, and pages them here.
const NOTE_FILTERS = ['all', 'credit', 'debit'] as const;
type NoteFilter = typeof NOTE_FILTERS[number];
const noteMatches = (n: AwPurchaseNoteRow, q: string) => { const k = q.trim().toLowerCase(); return !k || [n.note_no, n.reason, n.bill_no, n.vendor_name].some(v => String(v || '').toLowerCase().includes(k)); };
// The fallback's order (a server without GET /purchase-notes): the set in hand sorted by the SAME keys and the same
// rule as the route's NOTE_SORTS — names case-folded as NOCASE folds, then created_at and id in the same direction —
// so a list read either way reads in one order. With no sort, today's: newest note first.
const nocase = (v: unknown) => String(v ?? '').replace(/[A-Z]/g, c => c.toLowerCase());
const NOTE_SORT_VAL: Record<string, (n: AwPurchaseNoteRow) => string | number> = {
  date: n => String(n.note_date || ''), type: n => String(n.kind || ''), note_no: n => String(n.note_no || ''), bill_no: n => String(n.bill_no || ''),
  vendor: n => nocase(n.vendor_name), amount: n => Number(n.amount) || 0, vat: n => Number(n.vat_amount) || 0, by: n => nocase(n.created_by_name),
};
const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0);
const noteSortCmp = (sort: string | undefined, dir: string | undefined) => (a: AwPurchaseNoteRow, b: AwPurchaseNoteRow): number => {
  const val = sort ? NOTE_SORT_VAL[sort] : undefined;
  if (!val) return String(b.note_date).localeCompare(String(a.note_date)) || Number(b.created_at) - Number(a.created_at);
  const sign = dir === 'asc' ? 1 : -1;
  return sign * (cmp(val(a), val(b)) || cmp(Number(a.created_at) || 0, Number(b.created_at) || 0) || cmp(String(a.id), String(b.id)));
};
export function DebitCreditNotesPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<NoteFilter>('all');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwPurchaseNotePage | null>(null);
  const [fallback, setFallback] = useState(false);   // true once /purchase-notes answered 404: the fan-out is used from then on
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [dlg, setDlg] = useState(false);
  // (Batch 12 tail T1, FS-6) The note's own window (a read, every seat) and its edit (a writer's), each a sibling over the
  // mounted pane — closing either returns to the same filter, page and search.
  const [viewing, setViewing] = useState('');
  const [editing, setEditing] = useState<AwPurchaseNote | null>(null);
  // The note whose entries panel is open. Held here, not in the row's menu: the menu closes as it opens the panel (Documents.tsx).
  const [entriesOf, setEntriesOf] = useState<AwPurchaseNote | null>(null);
  // A read-only seat (a client's own books login, the auditor) reads the register and is drawn no write:
  // no +, and only what reads in its ⋯ — PayableNotes' `readOnly` rule, for the same reason.
  const ro = isReadOnlySeat(ctx);
  // Batch 12 tail T3 (FS-11): the register's column sort, on the server (and by the same keys in the fallback below).
  const ls = useListSort(ctx, 'purchases.notes', { onChange: () => setPage(1) });
  const { sort: sortK, dir: sortD } = sortArgs(ls);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    const gather = async (): Promise<AwPurchaseNotePage> => {
      const pays = (await loadAllPayables(ctx.clientId)).filter(p => Number(p.credit_total) > 0 || Number(p.debit_total) > 0);
      const out: AwPurchaseNoteRow[] = [];
      for (let i = 0; i < pays.length; i += 6) {
        const details: AwPayableDetail[] = await Promise.all(pays.slice(i, i + 6).map(p => api.awPayable(ctx.clientId, p.id)));
        for (const d of details) for (const n of d.notes || []) out.push({ ...n, bill_no: d.payable.bill_no, vendor_name: d.payable.vendor_name, currency: d.payable.currency });
      }
      const rows = out.filter(n => (kind === 'all' || n.kind === kind) && noteMatches(n, qd)).sort(noteSortCmp(sortK, sortD));
      return { rows: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit };
    };
    (async () => {
      try {
        let d: AwPurchaseNotePage;
        if (fallback) d = await gather();
        else {
          try { d = await api.awPurchaseNotes(ctx.clientId, { kind: kind === 'all' ? '' : kind, q: qd, page, limit, sort: sortK, dir: sortD }); }
          catch (e: any) { if (e?.status !== 404) throw e; if (alive) setFallback(true); d = await gather(); }
        }
        if (alive) { setData(d); setErr(''); }
      } catch (e: any) { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(perr(e, t)); } }
    })();
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, kind, qd, page, limit, fallback, sortK, sortD]);   // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (n: AwPurchaseNote) => {
    if (busy || !window.confirm(noteDeleteConfirm(n, t))) return;
    setBusy(n.id); setErr('');
    try { await api.awPayableNoteDelete(ctx.clientId, n.payable_id, n.id); ctx.bump(); } catch (e: any) { setErr(noteErr(e, t)); } finally { setBusy(''); }
  };
  // Batch 11 R8b: Book note / Unbook on this list too, by the same pre-checks as the bill's window. (Owner 2026-11-02, the seven columns) Now
  // read for one row as its Status dropdown or ⋯ opens, GET /doors, where the Actions column used to ask for every row of the page.
  const [booking, setBooking] = useState<{ n: AwPurchaseNoteRow; b: AwNoteBookable } | null>(null);
  const unbook = async (n: AwPurchaseNote) => {
    if (busy || !window.confirm(t('aw_pn_unbook_confirm'))) return;
    setBusy(`unbook-${n.id}`); setErr('');
    try { await api.awNoteUnbook(ctx.clientId, n.payable_id, n.id); ctx.bump(); } catch (e: any) { setErr(noteErr(e, t)); } finally { setBusy(''); }
  };
  const chips: OChip[] = NOTE_FILTERS.map(k => ({ key: k, label: t(`aw_note_filter_${k}`), active: k === kind, onClick: () => { setKind(k); setPage(1); } }));
  const rows = data?.rows || [];
  // ── One note's doors, on its Status dropdown and in its ⋯ (owner 2026-11-02, the seven columns) ─────────────────────────────────
  // NoteActions' conditions to the letter, drawn as items: a door that is shut is greyed with the sentence NoteActions printed in its place.
  const readDoors = (n: AwPurchaseNote) => (): Promise<AwNoteDoors> => api.awRowDoors(ctx.clientId, 'purchase_note', n.id);
  const bookDoor = (row: AwPurchaseNoteRow, v: AwNoteDoors | null, readError: string): RowMenuEntry[] => {
    const n: AwPurchaseNoteRow = { ...row, ...(v?.note || {}) };
    const b = n.book;
    if (!b) return [];
    const waiting = !v && !readError;
    const tax = hasTax(ctx);
    if (b.state !== 'booked') {
      const bookable = v?.bookable;
      return [{ key: 'book', icon: 'ti-book', label: t('aw_pn_book_btn'), waiting: tax && waiting, attrs: { 'data-pn-book': '' }, onPick: () => { if (bookable?.ok) setBooking({ n, b: bookable }); },
        withheld: !tax ? t('aw_pn_why_tax_module') : readError || (bookable && !bookable.ok ? noteWhy(bookable, t) : undefined) }];
    }
    return [{ key: 'unbook', icon: 'ti-book-off', label: t('aw_pn_unbook_btn'), waiting, attrs: { 'data-pn-unbook': '' }, onPick: () => unbook(n),
      withheld: readError || (b.unbookRefusal ? noteWhy(b.unbookRefusal, t) : !tax ? t('aw_pn_why_unbook_tax_module') : undefined) }];
  };
  const noteMenu = (row: AwPurchaseNoteRow, v: AwNoteDoors | null, readError: string): RowMenuEntry[] => {
    if (v && !v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const n: AwPurchaseNoteRow = { ...row, ...(v?.note || {}) };
    const items: RowMenuEntry[] = [
      { key: 'open', icon: 'ti-arrows-maximize', label: t('aw_er_open'), title: t(`aw_note_view_title_${n.kind}`), onPick: () => setViewing(n.id) },
      { key: 'entries', icon: 'ti-book-2', label: t('aw_ef_title'), title: t('aw_ef_btn_title'), onPick: () => setEntriesOf(n), attrs: { 'data-ef-btn': 'purchase_note' } },
    ];
    if (ro) return items;
    const booked = n.book?.state === 'booked';
    if (n.edit && (n.edit.figures || n.edit.reason)) items.push({ key: 'edit', icon: 'ti-pencil', label: t('aw_pn_edit'), attrs: { 'data-pn-edit': '' }, onPick: () => setEditing(n) });
    items.push(...bookDoor(row, v, readError));
    // Delete as before for a note not booked; a booked one's Delete takes its line out first, so it needs the Tax module and no refusal standing against that line.
    if (!booked || (hasTax(ctx) && !n.book?.deleteRefusal)) items.push({ key: 'delete', icon: 'ti-trash', danger: true, label: t('aw_bank_delete'), onPick: () => del(n), waiting: !v && !readError, withheld: readError || undefined });
    return items;
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OverflowChips chips={chips} chipStyle={chip} activeStyle={chipOn} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_note_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
        <div style={{ flex: 1 }} />
        {/* Top right, as on Sales (owner 2026-11-02, NewDocBtn.tsx); this list has no Export, so it stands alone. */}
        {!ro && <NewDocBtn onClick={() => setDlg(true)} title={t('aw_note_new_title')} />}
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_note_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                {/* Seven columns (owner 2026-11-02, "Standardized Document Table Layout"). Type is small text under the number; the bill is the
                    popover's; VAT, the reason and who recorded it are the note's own window (NoteFacts). Where it stands in the VAT return is its Status. */}
                <SortTh k="note_no" s={ls} label={t('aw_col_no')} /><SortTh k="date" s={ls} label={t('aw_col_date')} /><SortTh k="vendor" s={ls} label={t('aw_er_col_supplier')} />
                <SortTh k="amount" s={ls} align="right" label={t('aw_col_total')} /><th style={th}>{t('aw_col_currency')}</th>
                <th style={th}>{t('aw_col_status')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(n => {
                const entry = n.book?.state === 'booked' ? n.book.entry : null;
                return (
                  // The row opens the note's window; its own controls (the number, the Status dropdown, the menu) stop the click there.
                  <tr key={n.id} data-note-row={n.id} onClick={() => setViewing(n.id)} style={{ cursor: 'pointer' }}>
                    <NoCell sub={t(`aw_note_kind_${n.kind}`)} rel={{ ctx, kind: 'purchase_note', id: n.id, docNo: n.note_no, count: n.linked_count }}
                      no={<button type="button" data-pn-view={n.id} onClick={e => { e.stopPropagation(); setViewing(n.id); }} title={t(`aw_note_view_title_${n.kind}`)} style={noteLink}>{n.note_no || '—'}</button>} />
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(n.note_date)}</td>
                    <td style={td}>{n.vendor_name}</td>
                    <td style={num}>{amt(n.amount)}</td>
                    <td style={td}>{n.currency}</td>
                    {!n.book ? <td style={{ ...td, color: 'var(--text-tertiary)' }}>—</td> : (
                      <StatusCell<AwNoteDoors> word={t(n.book.state === 'booked' ? 'aw_pn_chip_booked' : 'aw_pn_chip_not_booked')} tone={n.book.state === 'booked' ? pillGreen : pillGrey} value={n.book.state} attrs={{ 'data-pn-chip': n.book.state }}
                        menu={!ro && hasTax(ctx) ? {
                          read: readDoors(n), busy: busy === `unbook-${n.id}`,
                          items: (v, why) => (v && !v.found ? [{ key: 'gone', note: t('aw_doc_menu_gone') }] : bookDoor(n, v, why)),
                        } : undefined}>
                        {n.book.state === 'booked' && <SmallLine name="entry">{entry ? t(entry.status === 'posted' ? 'aw_pn_entry_posted' : 'aw_pn_entry_draft', { no: entry.docNo }) : t('aw_pn_entry_none')}</SmallLine>}
                      </StatusCell>
                    )}
                    <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <RowMenu<AwNoteDoors> busy={busy === n.id} items={(v, why) => noteMenu(n, v, why)} read={ro ? undefined : readDoors(n)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_note_rule')}</div>
      {!ro && dlg && <NoteDialog ctx={ctx} onClose={() => setDlg(false)} onDone={() => { setDlg(false); ctx.bump(); }} />}
      {!ro && booking && <BookNoteDialog ctx={ctx} n={booking.n} bill={booking.n} bookable={booking.b} onClose={() => setBooking(null)} onDone={() => { setBooking(null); ctx.bump(); }} />}
      {viewing && <NoteViewDialog ctx={ctx} id={viewing} onClose={() => setViewing('')} />}
      {!ro && editing && <NoteDialog ctx={ctx} note={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); ctx.bump(); }} />}
      {entriesOf && <DocEntriesDialog ctx={ctx} kind="purchase_note" id={entriesOf.id} docNo={entriesOf.note_no} onClose={() => setEntriesOf(null)} />}
    </div>
  );
}

// ── Purchase order dialog (new · edit while draft) with the lines editor ────────────────────────
type LineDraft = { description: string; qty: string; unit_price: string };
const blankLine = (): LineDraft => ({ description: '', qty: '1', unit_price: '' });
const lineAmount = (l: LineDraft): number | null => {
  const q = fin(l.qty), u = fin(l.unit_price);
  if (q === null || u === null || Number.isNaN(q) || Number.isNaN(u)) return null;
  // The PO's own rule: line = qty × unit price AS TYPED, rounded to 2 dp — exactly what the server stores
  // (poLinesOf keeps the unit price as typed and rounds only the amount), so the total the operator
  // approves is the total the register holds. The unit price is therefore SENT unrounded below.
  return r2(q * u);
};
function PoDialog({ ctx, detail, onClose, onSaved }: { ctx: AwCtx; detail?: AwPurchaseOrderDetail; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const po = detail?.po;
  const [vendor, setVendor] = useState(po?.vendor_name || '');
  const [taxId, setTaxId] = useState(po?.vendor_tax_id || '');
  const [poDate, setPoDate] = useState(po?.po_date || todayIso());
  const [expected, setExpected] = useState(po?.expected_date || '');
  const [currency, setCurrency] = useState(po?.currency || '');   // blank = the company's default, applied by the server
  const [noteTxt, setNoteTxt] = useState(po?.note || '');
  const [lines, setLines] = useState<LineDraft[]>(detail?.lines?.length ? detail.lines.map(l => ({ description: l.description, qty: String(l.qty), unit_price: String(l.unit_price) })) : [blankLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — see BillDialog. An edit opens pre-filled with the order and its lines, which is given,
  // not typed: a diff against the opening values would ask about work nobody did.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const amounts = lines.map(lineAmount);
  const total = amounts.every(a => a !== null) ? r2(amounts.reduce((s, a) => s + (a as number), 0)) : null;
  const lineOk = (l: LineDraft) => { const q = fin(l.qty), u = fin(l.unit_price); return !!l.description.trim() && l.description.length <= 500 && q !== null && !Number.isNaN(q) && q > 0 && u !== null && !Number.isNaN(u) && u >= 0; };
  const curBad = !curOk(currency);
  const ready = !!vendor.trim() && [vendor, taxId].every(okStr) && !curBad && isoDate(poDate) && (!expected || isoDate(expected)) && lines.length >= 1 && lines.every(lineOk);
  // Every line field goes through here, so one mark covers the whole editor and no column can be
  // added that forgets to set it.
  const setLine = (i: number, patch: Partial<LineDraft>) => { setDirty(true); setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l)); };
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const body: AwPurchaseOrderBody = {
      vendor_name: vendor.trim(), vendor_tax_id: taxId.trim(), po_date: poDate, expected_date: expected, note: noteTxt.trim(),
      lines: lines.map((l): AwPurchaseOrderLineBody => ({ description: l.description.trim(), qty: Number(l.qty), unit_price: Number(l.unit_price) })),
    };
    if (currency.trim()) body.currency = currency.trim().toUpperCase();
    try {
      if (po) await api.awPurchaseOrderUpdate(ctx.clientId, po.id, body); else await api.awPurchaseOrderCreate(ctx.clientId, body);
      onSaved();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={po ? t('aw_po_edit_title') : t('aw_po_new_title')} docNo={po?.po_no} onClose={onClose} busy={busy} dirty={dirty} fullscreen
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={po ? 'ti ti-device-floppy' : 'ti ti-plus'} label={po ? t('aw_doc_save') : t('aw_po_create_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_vendor')}><input value={vendor} onChange={e => ed(setVendor, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_pay_vendor_tax_id')}><input value={taxId} onChange={e => ed(setTaxId, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_col_currency')}>
            <input value={currency} onChange={e => ed(setCurrency, e.target.value)} maxLength={3} placeholder={t('aw_pay_currency_default')} style={field} />
            <div style={{ ...note, marginTop: 3, color: curBad ? '#B42318' : undefined }}>{t('aw_pay_currency_hint')}</div>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 10 }}>
          <Field label={t('aw_po_date')}><input type="date" value={poDate} onChange={e => ed(setPoDate, e.target.value)} style={field} /></Field>
          <Field label={t('aw_po_expected')}><input type="date" value={expected} onChange={e => ed(setExpected, e.target.value)} style={field} /></Field>
          <Field label={t('aw_bank_note_opt')}><input value={noteTxt} onChange={e => ed(setNoteTxt, e.target.value)} style={field} /></Field>
        </div>
        <div>
          <label style={lbl}>{t('aw_po_lines')}</label>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }}>#</th><th style={th}>{t('aw_po_line_description')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>{t('aw_po_qty')}</th><th style={{ ...th, textAlign: 'right', width: 140 }}>{t('aw_po_unit_price')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>{t('aw_bank_amount')}</th><th style={{ ...th, width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...td, color: 'var(--text-tertiary)' }}>{i + 1}</td>
                    <td style={td}><input value={l.description} onChange={e => setLine(i, { description: e.target.value })} maxLength={500} style={field} /></td>
                    <td style={td}><input type="number" step="any" min="0" value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} style={{ ...field, textAlign: 'right' }} /></td>
                    <td style={td}><input type="number" step="any" min="0" value={l.unit_price} onChange={e => setLine(i, { unit_price: e.target.value })} style={{ ...field, textAlign: 'right' }} /></td>
                    <td style={{ ...num, fontWeight: 600 }}>{amounts[i] === null ? '—' : amt(amounts[i])}</td>
                    <td style={td}>
                      <button onClick={() => ed(setLines, ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : [blankLine()])} title={t('aw_po_remove_line')} style={{ ...actBtn, padding: '4px 7px', color: '#B42318' }}><i className="ti ti-x" aria-hidden /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ ...td, borderBottom: 'none' }}>
                    <button onClick={() => ed(setLines, ls => [...ls, blankLine()])} style={actBtn}><i className="ti ti-plus" aria-hidden /> {t('aw_po_add_line')}</button>
                  </td>
                  <td style={{ ...num, borderBottom: 'none', fontWeight: 800, fontSize: 13.5 }}>{total === null ? '—' : amt(total)}</td>
                  <td style={{ ...td, borderBottom: 'none' }} />
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={note}>{t('aw_po_lines_rule')}</div>
        </div>
        {!!po && <RecordEvidence clientId={ctx.clientId} kind="purchase_order" id={po.id} />}
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── PO lines (read-only) ────────────────────────────────────────────────────────────────────────
export function PoViewDialog({ ctx, id, onClose, onEdit }: { ctx: AwCtx; id: string; onClose: () => void;
  /** The list's way to its editor. Absent where the window was reached from another document (relatedDocs.tsx), which has no editor to hand it to. */
  onEdit?: (d: AwPurchaseOrderDetail) => void;
}) {
  const { t } = useTranslation();
  const [d, setD] = useState<AwPurchaseOrderDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    // Asked first, as the other windows a related document opens are (sourceDoc.tsx stillStands): a pinned panel may be minutes old.
    stillStands(ctx.clientId, 'purchase_order', id)
      .then(there => {
        if (!alive) return null;
        if (!there) { setErr(t('aw_doc_menu_gone')); return null; }
        return api.awPurchaseOrder(ctx.clientId, id).then((x: AwPurchaseOrderDetail) => { if (alive) setD(x); });
      })
      .catch((e: any) => { if (alive) setErr(perr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const po = d?.po;
  return (
    <Dialog title={po ? po.vendor_name : t('aw_sub_purchase_orders')} docNo={po?.po_no} onClose={onClose} fullscreen
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <GateCancel onClose={onClose} label={t('aw_bank_close')} />
          {/* The window itself is a read (the PO and its lines); its Edit is a writer's door only. */}
          {!isReadOnlySeat(ctx) && po?.status === 'draft' && d && onEdit && <button onClick={() => onEdit(d)} style={btnPrimary}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>}
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div style={errBox}>{err}</div>}
        {!d || !po ? (!err && <Loader />) : (
          <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, alignItems: 'baseline' }}>
              <span style={poPill(po.status)}>{t(`aw_po_status_${po.status}`)}</span>
              <span>{t('aw_po_date')}: <b>{dmy(po.po_date)}</b></span>
              {po.expected_date && <span>{t('aw_po_expected')}: <b>{dmy(po.expected_date)}</b></span>}
              {po.vendor_tax_id && <span>{t('aw_pay_vendor_tax_id')}: {po.vendor_tax_id}</span>}
              {po.payable_id && <span style={pillGreen}><i className="ti ti-file-invoice" aria-hidden /> {t('aw_po_status_billed')}</span>}
            </div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={table}>
                <thead><tr><th style={{ ...th, width: 28 }}>#</th><th style={th}>{t('aw_po_line_description')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_po_qty')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_po_unit_price')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th></tr></thead>
                <tbody>
                  {d.lines.map(l => (
                    <tr key={l.id}><td style={{ ...td, color: 'var(--text-tertiary)' }}>{l.line_no}</td><td style={td}>{l.description}</td><td style={num}>{l.qty}</td><td style={num}>{unitPrice(l.unit_price)}</td><td style={num}>{amt(l.amount)}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={4} style={{ ...num, borderBottom: 'none', fontWeight: 700 }}>{t('aw_col_total')}</td><td style={{ ...num, borderBottom: 'none', fontWeight: 800, fontSize: 13.5 }}>{amt(po.total_amount)} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{po.currency}</span></td></tr></tfoot>
              </table>
            </div>
            {po.note && <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{po.note}</div>}
            <div style={note}>{po.created_by_name} · {when(po.created_at)}</div>
            <RecordEvidence clientId={ctx.clientId} kind="purchase_order" id={po.id} />
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── Convert a PO to a bill ──────────────────────────────────────────────────────────────────────
function ConvertDialog({ ctx, po, onClose, onDone }: { ctx: AwCtx; po: AwPurchaseOrder; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [billNo, setBillNo] = useState('');
  const [billDate, setBillDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [vat, setVat] = useState('');
  const [wht, setWht] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — see BillDialog. The bill date opens on today, which is given, not typed.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  const nVat = fin(vat), nWht = fin(wht);
  const total = Number(po.total_amount) || 0;
  const whtOver = nWht !== null && !Number.isNaN(nWht) && nWht > total;
  const ready = !!billNo.trim() && okStr(billNo) && isoDate(billDate) && (!dueDate || isoDate(dueDate))
    && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0)) && (nWht === null || (!Number.isNaN(nWht) && nWht >= 0 && !whtOver));
  const toPay = ready ? r2(total - (nWht || 0)) : null;   // the register's rule, shown before the server stores it
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      // (Owner 2026-11-02) This window may have stood open for minutes, and a refused call pages the admins: asked first.
      const v: AwPoDoors = await api.awRowDoors(ctx.clientId, 'purchase_order', po.id);
      if (!v.found || v.status === 'billed' || v.status === 'cancelled') { setErr(t('aw_list_status_moved', { no: po.po_no })); setBusy(false); ctx.bump(); return; }
      await api.awPurchaseOrderConvert(ctx.clientId, po.id, { bill_no: billNo.trim(), bill_date: billDate, due_date: dueDate || undefined, vat_amount: r2(nVat || 0), wht_amount: r2(nWht || 0) });
      onDone();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_po_convert_title')} docNo={po.po_no} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-file-invoice" label={t('aw_po_convert_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 860 }}>
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ fontWeight: 700 }}>{po.po_no}</span><span style={{ flex: '1 1 160px' }}>{po.vendor_name}</span>
          <span style={{ whiteSpace: 'nowrap' }}>{t('aw_col_total')}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(po.total_amount)} {po.currency}</b></span>
        </div>
        <div style={note}>{t('aw_po_convert_hint')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_bill_no')}><input value={billNo} onChange={e => ed(setBillNo, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_pay_bill_date')}><input type="date" value={billDate} onChange={e => ed(setBillDate, e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_due_date')}><input type="date" value={dueDate} onChange={e => ed(setDueDate, e.target.value)} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_vat')}><input type="number" step="0.01" min="0" value={vat} onChange={e => ed(setVat, e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_wht')}><input type="number" step="0.01" min="0" value={wht} onChange={e => ed(setWht, e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_to_pay')}><div style={{ ...field, fontWeight: 800, fontVariantNumeric: 'tabular-nums', background: 'var(--bg-app)' }}>{toPay === null ? '—' : amt(toPay)}</div></Field>
        </div>
        {whtOver && <div style={{ fontSize: 11.5, color: '#B42318' }}>{t('aw_pay_wht_over')}</div>}
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Purchases › Purchase orders ─────────────────────────────────────────────────────────────────
export function PurchaseOrdersPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AwPurchaseOrderFilter>('all');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwPurchaseOrderPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [dlg, setDlg] = useState<{ kind: 'new' } | { kind: 'edit'; detail: AwPurchaseOrderDetail } | { kind: 'view'; id: string } | { kind: 'convert'; po: AwPurchaseOrder } | { kind: 'cancel'; po: AwPurchaseOrder } | null>(null);
  // Batch 12 tail T3 (FS-11): the orders' column sort, on the server.
  const ls = useListSort(ctx, 'purchases.orders', { onChange: () => setPage(1) });
  const { sort: sortK, dir: sortD } = sortArgs(ls);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awPurchaseOrders(ctx.clientId, { status: status === 'all' ? '' : status, q: qd, page, limit, sort: sortK, dir: sortD })
      .then((d: AwPurchaseOrderPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, status, qd, page, limit, sortK, sortD]);   // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  // Edit needs the lines, which the list does not carry: fetch the detail, then open the editor (a read — no re-read signal).
  const edit = async (po: AwPurchaseOrder) => {
    if (busy) return; setBusy(po.id); setErr('');
    try { const d: AwPurchaseOrderDetail = await api.awPurchaseOrder(ctx.clientId, po.id); setDlg({ kind: 'edit', detail: d }); }
    catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  const chips: OChip[] = PO_FILTERS.map(s => ({ key: s, label: t(`aw_po_filter_${s}`), active: s === status, onClick: () => { setStatus(s); setPage(1); } }));
  const rows = data?.rows || [];
  const done = () => { setDlg(null); ctx.bump(); };
  const canConvert = (po: { status: string }) => po.status !== 'billed' && po.status !== 'cancelled';
  // A read-only seat reads the orders and opens one (the PO number, or Open in its ⋯) — and is drawn none of the writes:
  // no +, no Edit / Approve / Receive / Convert / Cancel, no Status dropdown, no Edit in the window.
  const ro = isReadOnlySeat(ctx);
  // ── One order's doors, on its Status dropdown and in its ⋯ (owner 2026-11-02, the seven columns) ──────────────────────────────────
  // The buttons this column used to hold, with their conditions and their dialogs. Billed is never picked: only Convert makes it. The
  // conditions are asked of the order as it stands when either opens (GET /doors), since a click on a stale one would be a refused 409, which pages the admins.
  const readDoors = (po: AwPurchaseOrder) => (): Promise<AwPoDoors> => api.awRowDoors(ctx.clientId, 'purchase_order', po.id);
  const statusDoors = (po: AwPurchaseOrder, v: AwPoDoors | null, readError: string): RowMenuEntry[] => {
    const st = v?.status || po.status;
    const wait = { waiting: !v && !readError, withheld: readError || undefined };
    return [
      ...(st === 'draft' ? [{ key: 'approve', icon: 'ti-check', label: t('aw_po_approve'), ...wait, onPick: () => act(po.id, () => api.awPurchaseOrderApprove(ctx.clientId, po.id)) }] : []),
      ...(st === 'approved' ? [{ key: 'receive', icon: 'ti-package', label: t('aw_po_receive'), ...wait, onPick: () => act(po.id, () => api.awPurchaseOrderReceive(ctx.clientId, po.id)) }] : []),
      ...(canConvert({ status: st }) ? [{ key: 'cancel', icon: 'ti-ban', danger: true, label: t('aw_po_cancel_btn'), ...wait, onPick: () => setDlg({ kind: 'cancel', po }) }] : []),
    ];
  };
  const poMenu = (po: AwPurchaseOrder, v: AwPoDoors | null, readError: string): RowMenuEntry[] => {
    if (v && !v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const open: RowMenuEntry = { key: 'open', icon: 'ti-arrows-maximize', label: t('aw_er_open'), onPick: () => setDlg({ kind: 'view', id: po.id }) };
    if (ro) return [open];
    const st = v?.status || po.status;
    const wait = { waiting: !v && !readError, withheld: readError || undefined };
    const doors = statusDoors(po, v, readError);
    return [
      open,
      ...(st === 'draft' ? [{ key: 'edit', icon: 'ti-pencil', label: t('aw_profile_edit'), ...wait, onPick: () => edit(po) }] : []),
      ...doors.filter(d => d.key !== 'cancel'),
      ...(canConvert({ status: st }) ? [{ key: 'convert', icon: 'ti-file-invoice', label: t('aw_po_convert_btn'), ...wait, onPick: () => setDlg({ kind: 'convert', po }) }] : []),
      ...doors.filter(d => d.key === 'cancel'),
    ];
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OverflowChips chips={chips} chipStyle={chip} activeStyle={chipOn} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_po_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
        <div style={{ flex: 1 }} />
        {/* Top right, as on Sales (owner 2026-11-02, NewDocBtn.tsx); this list has no Export, so it stands alone. */}
        {!ro && <NewDocBtn onClick={() => setDlg({ kind: 'new' })} title={t('aw_po_new_title')} />}
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_po_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                {/* Seven columns (owner 2026-11-02, "Standardized Document Table Layout"). The expected date and the lines are the order's own window (PoViewDialog). */}
                <SortTh k="po_no" s={ls} label={t('aw_col_no')} /><SortTh k="po_date" s={ls} label={t('aw_col_date')} /><SortTh k="vendor" s={ls} label={t('aw_er_col_supplier')} />
                <SortTh k="total" s={ls} align="right" label={t('aw_col_total')} /><th style={th}>{t('aw_col_currency')}</th>
                <SortTh k="status" s={ls} label={t('aw_col_status')} /><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(po => (
                <tr key={po.id}>
                  <NoCell rel={{ ctx, kind: 'purchase_order', id: po.id, docNo: po.po_no, count: po.linked_count }}
                    no={<button onClick={() => setDlg({ kind: 'view', id: po.id })} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 12.5 }}>{po.po_no}</button>} />
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(po.po_date)}</td>
                  <td style={td}>{po.vendor_name}{po.vendor_tax_id && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{po.vendor_tax_id}</div>}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{amt(po.total_amount)}</td>
                  <td style={td}>{po.currency}</td>
                  <StatusCell<AwPoDoors> word={t(`aw_po_status_${po.status}`)} tone={poPill(po.status)} value={po.status}
                    menu={!ro && canConvert(po) ? { read: readDoors(po), busy: busy === po.id, items: (v, why) => {
                      if (v && !v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
                      const doors = statusDoors(po, v, why);
                      return doors.length ? doors : [{ key: 'none', note: t('aw_list_status_no_door') }];
                    } } : undefined} />
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <RowMenu<AwPoDoors> busy={busy === po.id} items={(v, why) => poMenu(po, v, why)} read={ro ? undefined : readDoors(po)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_po_note')}</div>
      {!ro && dlg?.kind === 'new' && <PoDialog ctx={ctx} onClose={() => setDlg(null)} onSaved={done} />}
      {!ro && dlg?.kind === 'edit' && <PoDialog ctx={ctx} detail={dlg.detail} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'view' && <PoViewDialog ctx={ctx} id={dlg.id} onClose={() => setDlg(null)} onEdit={d => setDlg({ kind: 'edit', detail: d })} />}
      {!ro && dlg?.kind === 'convert' && <ConvertDialog ctx={ctx} po={dlg.po} onClose={() => setDlg(null)} onDone={done} />}
      {!ro && dlg?.kind === 'cancel' && (
        <ReasonDialog title={t('aw_po_cancel_title')} docNo={dlg.po.po_no} line={<><b style={{ color: 'var(--text-primary)' }}>{dlg.po.po_no}</b> · {dlg.po.vendor_name} · {amt(dlg.po.total_amount)} {dlg.po.currency}</>}
          hint={t('aw_po_cancel_hint')} icon="ti ti-ban" label={t('aw_po_cancel_btn')} onClose={() => setDlg(null)}
          onSubmit={async reason => {
            // The reason takes a while to type, and a refused call pages the admins: asked again at the click (Documents.tsx endDoc).
            const v: AwPoDoors = await api.awRowDoors(ctx.clientId, 'purchase_order', dlg.po.id);
            if (!v.found || !canConvert({ status: String(v.status) })) { ctx.bump(); throw new Error(t('aw_list_status_moved', { no: dlg.po.po_no })); }
            await api.awPurchaseOrderCancel(ctx.clientId, dlg.po.id, reason); done();
          }} />
      )}
    </div>
  );
}

// ── Purchases › Supplier bills (the portal uploads) ─────────────────────────────────────────────
// The bills the company uploaded through its own portal (merchant_expense_uploads), as stored:
// pending / approved / rejected with the reject reason, and the OCR vendor / amount / date where the
// portal recorded them. "Create bill" (in the row's ⋯) opens the payable form pre-filled from the upload's
// own fields; once one links to it, the related-documents icon beside the number shows the bill.
const STATUSES = ['', 'pending', 'approved', 'rejected'] as const;
const sPill = (s: string): React.CSSProperties => s === 'approved' ? pillGreen : s === 'rejected' ? pillRed : pillAmber;

export function SupplierBillsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('');
  const [rows, setRows] = useState<AwExpense[] | null>(null);
  // Paged like the Sales tables: the server answers total/limit, the pager is driven by its limit.
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [paging, setPaging] = useState<{ total: number; limit: number }>({ total: 0, limit });
  // EXP-7 (batch 7 R3a): both numbers are searchable — the EXP number and the supplier's own.
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [billed, setBilled] = useState<Map<string, AwPayable> | null>(null);
  const [creating, setCreating] = useState<AwExpense | null>(null);
  // The scan whose entries panel is open. Held here, not in the row's menu: the menu closes as it opens the panel (Documents.tsx).
  const [entriesOf, setEntriesOf] = useState<AwExpense | null>(null);
  // A row opens the SAME expense record window Expenses › Expense records opens (EXP-16), on ?exp=.
  const rec = useRecordParam();
  // The books seat reads, never writes (middleware.ts refuses its every non-GET with a JSON 403 the admins
  // are paged with): no "Create bill" for it — Documents.tsx's rule for a read-only login.
  const readOnly = isReadOnlySeat(ctx);
  // Batch 12 tail T3 (FS-11): the scans' column sort, on the server.
  const ls = useListSort(ctx, 'purchases.supplier_bills', { onChange: () => setPage(1) });
  const { sort: sortK, dir: sortD } = sortArgs(ls);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    // ⛔ The rows in hand stay on screen while the next ones are read (as every other list here does) — blanking
    // them to a loader unmounted the table under the header just clicked, and the keyboard focus with it (T3-7).
    let alive = true;
    api.awExpenses(ctx.clientId, status, { page, limit, q: qd, sort: sortK, dir: sortD }).then((d: any) => { if (alive) { setRows(d.rows || []); setPaging({ total: Number(d.total) || 0, limit: Number(d.limit) || limit }); setErr(''); } }).catch((e: any) => { if (alive) { setRows([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, status, page, limit, qd, ctx.reload, sortK, sortD]);
  // Which uploads already have a bill: read off the payables register (no lookup route in the contract).
  useEffect(() => {
    let alive = true;
    loadAllPayables(ctx.clientId).then(ps => { if (alive) setBilled(byUpload(ps)); }).catch(() => { if (alive) setBilled(new Map()); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  const openFile = async (r: AwExpense) => {
    if (busy) return; setBusy(r.id); setErr('');
    try { openBlob(await api.awExpenseFileBlobUrl(ctx.clientId, r.id)); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const label = (s: string) => s === '' ? t('aw_exp_all') : t(`aw_exp_${s}`);
  // The buttons in a row act on their own; the row itself opens the record.
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  // The scan's doors on its Status dropdown (owner 2026-11-02): the record window's own Approve, Return and Void.
  const doors = useRecordDoors(ctx);
  const noOf = (r: AwExpense) => r.expense_no || r.doc_no || r.filename;
  // What this row's Actions column used to hold, in its ⋯ (owner 2026-11-02, the seven columns): Create bill under its old conditions, and
  // what only reads for every seat. The bill it was made into is the related-documents popover's now, and it opens from there.
  const rowItems = (r: AwExpense): RowMenuEntry[] => {
    const link = billed?.get(r.id) || null;
    return [
      { key: 'open', icon: 'ti-arrows-maximize', label: t('aw_er_open'), title: t('aw_er_open_title'), onPick: () => rec.open(`u:${r.id}`) },
      { key: 'file', icon: busy === r.id ? 'ti-loader' : 'ti-file-search', label: t('aw_exp_open_file'), onPick: () => openFile(r), waiting: !!busy },
      ...(!readOnly && !r.cancelled && (r.status === 'pending' || r.status === 'approved') && !link
        ? [{ key: 'create_bill', icon: 'ti-file-plus', label: t('aw_pay_create_from_upload'), onPick: () => setCreating(r), waiting: billed === null || !!busy }] : []),
      /* EXP-15 / COA-D2 — what this uploaded bill posted. `expense_upload` is one of the four routes services/journalLinks.ts resolves a
         tax line by (the upload points AT the line, not the other way round), so the question is the resolver's to answer on every row: an
         upload nobody has approved yet is told "nothing posted from this document yet", which is the truth. */
      { key: 'entries', icon: 'ti-book-2', label: t('aw_ef_title'), title: t('aw_ef_btn_title'), onPick: () => setEntriesOf(r), attrs: { 'data-ef-btn': 'expense_upload' } },
    ];
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {STATUSES.map(s => (
          <button key={s} onClick={() => { setStatus(s); setPage(1); }} style={{ ...btn, padding: '5px 11px', fontSize: 12, ...(status === s ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>{label(s)}</button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_er_sb_search_ph')} aria-label={t('aw_er_sb_search_ph')} style={{ ...inp, flex: '1 1 220px', maxWidth: 380 }} />
        {/* This list's default order, newest upload first, has no header since the seven columns: only while a header's sort is on, the way back to it
            (LedgerAccounts.tsx's "Chart order"). A scan with no date would otherwise sink out of reach, with no click to bring it back. */}
        {!ls.isDefault && (
          <button type="button" onClick={ls.reset} data-sort-reset="" style={{ ...btn, padding: '5px 11px', fontSize: 12 }}>
            <i className="ti ti-sort-descending" aria-hidden /> {t('aw_sort_default_upload')}
          </button>
        )}
      </div>
      {err && <div style={errBox}>{err}</div>}
      {doors.notice}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {rows === null ? <Loader />
          : !rows.length ? <div style={emptyLine}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                {/* Seven columns (owner 2026-11-02, "Standardized Document Table Layout"). The supplier's number is small text under ours, the
                    reason under the status; the file and when it was uploaded are the record's window, and its bill the popover's and that window's. */}
                <SortTh k="expense_no" s={ls} label={t('aw_col_no')} /><SortTh k="doc_date" s={ls} label={t('aw_col_date')} /><SortTh k="vendor" s={ls} label={t('aw_er_col_supplier')} />
                <SortTh k="total" s={ls} align="right" label={t('aw_col_total')} /><th style={th}>{t('aw_col_currency')}</th>
                <SortTh k="status" s={ls} label={t('aw_col_status')} /><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} onClick={() => rec.open(`u:${r.id}`)} title={t('aw_er_open_title')} style={{ cursor: 'pointer' }}>
                  <NoCell sub={r.doc_no || undefined} rel={{ ctx, kind: 'expense_upload', id: r.id, docNo: noOf(r), count: r.linked_count }}
                    no={r.expense_no ? <span style={{ color: 'var(--accent)' }}>{r.expense_no}</span> : <NoNumber title={t('aw_er_no_number')} />} />
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.doc_date ? dmy(r.doc_date) : '—'}</td>
                  <td style={td}>{r.vendor || ''}</td>
                  <td style={num}>{r.total === null || r.total === undefined ? '' : amt(r.total)}</td>
                  <td style={td}>{r.currency}</td>
                  <StatusCell<AwRecordDoors> word={r.cancelled ? t('aw_er_chip_cancelled') : (t(`aw_exp_${r.status}`) || r.status)} tone={r.cancelled ? pillRed : sPill(r.status)} value={r.status} attrs={r.cancelled ? { 'data-cancelled': '1' } : {}}
                    menu={!readOnly && !r.cancelled && (r.status === 'pending' || r.status === 'approved') ? doors.menu(`u:${r.id}`, noOf(r)) : undefined}>
                    {!!r.reject_reason && <SmallLine name="reason" tone="#B42318">{r.reject_reason}</SmallLine>}
                  </StatusCell>
                  <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={stop}>
                    <RowMenu busy={busy === r.id} items={() => rowItems(r)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {rows !== null && <Pagination page={page} total={paging.total} limit={paging.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_exp_note')}</div>
      {creating && <BillDialog ctx={ctx} upload={creating} onClose={() => setCreating(null)} onSaved={() => { setCreating(null); ctx.bump(); }} />}
      {doors.dialog}
      {entriesOf && <DocEntriesDialog ctx={ctx} kind="expense_upload" id={entriesOf.id} docNo={noOf(entriesOf)} onClose={() => setEntriesOf(null)} />}
      {rec.key && <ExpenseRecordWindow ctx={ctx} recordKey={rec.key} onClose={rec.close} />}
    </div>
  );
}
